import { messageHasFinalManuscriptChange } from "./manuscriptChangeMessageRef";
import { mergeFrozenChatReplies } from "./frozenChatReplies";
import { interruptedChatRetryUserMessage } from "./interruptedChatRetryUserMessage";
import * as Sentry from "@sentry/react";
import { dismissImportConversationStarters } from "./dismissImportConversationStarters";
import { useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { default as InlineSVG } from "react-inlinesvg";
import { AppEventPublisher, AgenticChatMode } from "src/AppEventPublisher";
import Auth from "src/Auth";
import { resolveRevertVisibility } from "./chatRevertVisibility";
import { resolveConsentReplaySelection } from "src/lib/consentReplaySelection";
import {
  readPendingManuscriptDraftReplayFromAssistantMetadata,
} from "./placementSessionMetadata";
import { cn } from "src/lib/cn";
import { useOnSudoLayoutEvent } from "src/lib/layout-events";
import { normalizeTerminalPlanStatus, resolvePlanCurrentStepIndex } from "src/lib/normalizeTerminalPlanStatus";
import { usePersistentChatStore, type PlanData } from "src/stores/persistentChatStore";
import { useProjectStore } from "src/stores/projectStore";
import { useSudowriteStore } from "src/stores/sudowriteStore";
import { Text } from "src/sudokit/components/Text";
import {
  ChatComposerShell as SharedChatComposerShell,
  ChatMessagesList as SharedChatMessagesList,
  ChatPanel as SharedChatPanel,
  ThinkingAccordion,
  thinkingStepChromeIcons,
} from "@sudowrite/shared-chat";
import { useStructuredOutlineStore } from "../StoryBible/StructuredOutline/StructuredOutlineStore";
import { openChatPreferences } from "src/lib/openChatPreferences";
import { ConversationStarters } from "./ConversationStarters";
import { isCommentingKeepingChatTurnOpen } from "./commentingTurnState";
import { WithReset, WithResetMotion } from "src/sudokit/Layout/BoxReset";

import { getChatComposerModeLabels as getAgenticChatModeLabels } from "@sudowrite/shared-chat/labels";
import { PersistentChatMessageData } from "~shared/types/persistent-chat-types";
import { branchTipMessageId, userBranchVariantsAtParent, userMessageIdsWithVisibleAssistantReply } from "~shared/persistentChatBranching";
import {
  isTrialAgenticChatSendBlocked,
  isTrialDiscussModeBlocked,
  isTrialFreeChatBlocked,
  subscriptionViewFromClientJSON,
} from "~shared/trialFreeChatAccess";
import { entityGenerateCtaActionFromUserMessage } from "src/lib/entityGenerateCtaAction";
import { buildEntityGenerateUserMessageFromCtaAction } from "~shared/agenticChat/entityGenerateUserMessageContent";
import { useToast } from "../ToastWrapper";
import { usePersistentChat, AgenticToolRun, type AttachmentDoc } from "./PersistentChatContext";
import { ChatComposer, ComposerAttachmentStrip, type ChatComposerHandle } from "./ChatComposer";
import { PersistentChatMessage } from "./PersistentChatMessage";
import {
  DIFFERENT_APPROACH_RETRY_MESSAGE,
} from "./agenticFeedbackRecovery";
import { NEW_CHAT_ID } from "./PersistentChatTypes";
import { usePersistentChatUserSettings } from "./PersistentChatUserSettings";
import { useSetActiveBranch } from "./PersistentChatQueries";
import { useMemories } from "./ChatPersonalization/useMemories";
import {
  buildResponseLanguagePreferenceMemory,
  getLanguageDisplayName,
  normalizeLanguageCode,
  parseResponseLanguagePreferenceMemory,
} from "./ChatPersonalization/languagePreferenceMemory";
import { PlanCard } from "./Plan/PlanCard";
import { CommentingCard } from "./CommentingCard";
import { StoryBibleCtaCard } from "./StoryBibleCtaCard";
import {
  findAssistantMessageForUserQuestion,
  isStoryBibleCtaType,
  shouldStartGenerateCastProgressOnQuestionAnswer,
} from "./storyBibleCta";
import { parseStoryBibleGeneratedEntity } from "~shared/agenticChat/storyBibleGeneratedEntity";
import { messageHasRewriteSuggestionCarouselMetadata } from "src/components/RewriteSuggestionCarousel/rewriteSuggestionParser";
import { BottomActionToast } from "./BottomActionToast";
import { SuggestedActionCard } from "./SuggestedActionCard";
import {
  AskUserQuestion,
  appendDocumentMentionsForAmbiguousManuscriptQuestions,
  formatUserQuestionAnswerBlock,
  type AppendedDocumentMentionsResult,
  type UserQuestionAnswerPair,
} from "./AskUserQuestion";
import { ImportFeedback } from "./ImportFeedback";
import { BlockingBottomSheetOverlay } from "./BlockingBottomSheetOverlay";
import {
  questionIsFeedbackTypesPicker,
  USER_QUESTION_NO_PREFERENCE,
} from "./manuscriptFeedbackTypePicker";
import { userQuestionAnswerAffirmsAllowEditsSwitch } from "./userQuestionModeSwitch";
import {
  clearAutoSwitchedFromChatToWrite,
  consumeAutoSwitchedFromChatToWrite,
  markAutoSwitchedFromChatToWrite,
  peekAutoSwitchedFromChatToWrite,
} from "./modeSwitchBackToast";
import {
  clearStoryBibleCtaAutoPromotedFromChat,
  getComposerAgenticChatMode,
  promoteComposerToAllowEditsForStoryBibleCta,
  restoreComposerToChatOnlyIfStoryBibleCtaCancelledBeforeRun,
} from "./storyBibleCtaModeSwitch";
import { ThinkingStep } from "./ThinkingStep";
import { useStoryBibleStore } from "src/stores/storyBibleStore";
import { useCrossAppSignalStore } from "src/stores/crossAppSignalStore";
import { SPRING_BOUNCY, FADE_FAST } from "./Plan/animations";
import { scrollToLastMessage } from "./scrollToLastMessage";
import {
  isCtaSeededUserMessage,
  isExpectedNewChatMaterialization,
  shouldResetPersistentChatOnSameProjectChatChange,
  stashSnapshotForUnansweredQuestion,
} from "./persistentChatSwitchPolicy";
import {
  isAssistantQuestionAnsweredInMessages,
  resolveQuestionMessageIdForAnsweredCheck,
} from "./assistantQuestionAnswered";
import { selectTerminalPlanCardMessageIds, selectTerminalCommentingCardMessageIds } from "./planCardVisibility";
import {
  getLastAssistantMessageForPlanSync,
  resolvePersistedPlanForSync,
} from "./planMessageSync";
import {
  clearDismissedUserQuestionSignatures,
  getDismissedUserQuestionMessageIds,
  isDismissedUserQuestion,
  isDismissedUserQuestionSignature,
  markDismissedUserQuestion,
  markDismissedUserQuestionSignature,
} from "./dismissedUserQuestionStorage";
import {
  collectAnsweredQuestionMessageIdsFromHistory,
  resolvePendingQuestionMessageToRestore,
  shouldKeepLiveQuestionWithUserMessageTail,
} from "./userQuestionRestore";
import { useImportFeedbackOverlay } from "./useImportFeedbackOverlay";
import { ChatExperienceSurvey } from "./ChatExperienceSurvey";
import { getFloatingSurveyScrollReserve } from "./floatingSurveyLayout";
import { useChatExperienceSurvey } from "./useChatExperienceSurvey";
import { FLOATING_CHAT_LAYER_Z_INDEX } from "./floatingChatLayerOrder";
import { gateImportReportDocumentId } from "./gateImportReportDocumentId";

const EMPTY_ARRAY: any[] = [];
const EMPTY_STRING_ARRAY: string[] = [];

type ConsentEditorSelectionHint = {
  documentId: string;
  pmFrom?: number;
  pmTo?: number;
  plainTextStart?: number;
  plainTextEnd?: number;
};

function normalizeConsentEditorSelectionHint(raw: unknown): ConsentEditorSelectionHint | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const hint = raw as Record<string, unknown>;
  const documentId = typeof hint.documentId === "string" ? hint.documentId.trim() : "";
  const pmFrom = Number(hint.pmFrom);
  const pmTo = Number(hint.pmTo);
  const plainTextStart = Number(hint.plainTextStart);
  const plainTextEnd = Number(hint.plainTextEnd);
  if (!documentId) return undefined;

  const hasPmRange = Number.isFinite(pmFrom) && Number.isFinite(pmTo) && pmTo > pmFrom;
  const hasPlainTextRange =
    Number.isFinite(plainTextStart)
    && Number.isFinite(plainTextEnd)
    && plainTextEnd > plainTextStart;
  const hasPlainTextStart = Number.isFinite(plainTextStart);
  if (!hasPmRange && !hasPlainTextRange && !hasPlainTextStart) return undefined;

  return {
    documentId,
    ...(hasPmRange
      ? {
          pmFrom: Math.floor(pmFrom),
          pmTo: Math.floor(pmTo),
        }
      : {}),
    ...(hasPlainTextRange
      ? {
          plainTextStart: Math.floor(plainTextStart),
          plainTextEnd: Math.floor(plainTextEnd),
        }
      : hasPlainTextStart
        ? { plainTextStart: Math.floor(plainTextStart) }
        : {}),
  };
}

function resolveConsentReplayFromActiveHighlight(
  replayPassage: string,
  replayEditorSelectionHint: ConsentEditorSelectionHint | undefined,
): { passage: string; hint?: ConsentEditorSelectionHint } {
  const chipState = usePersistentChatStore.getState();
  return resolveConsentReplaySelection({
    replayPassage,
    replayEditorSelectionHint,
    chipPassage: chipState.activeHighlight?.trim() ?? "",
    chipHint: normalizeConsentEditorSelectionHint(chipState.activeHighlightSelectionHint),
  });
}

function readPendingManuscriptDraftFromAssistantMetadata(metadata: unknown): {
  passage?: string;
  editorSelectionHint?: ConsentEditorSelectionHint;
} | null {
  return readPendingManuscriptDraftReplayFromAssistantMetadata(metadata);
}

function findPendingDraftReplayForQuestionAnswer(
  messages: Array<{ role?: string; metadata?: unknown }> | null | undefined,
  answeredAssistant: { role?: string; metadata?: unknown } | undefined,
): { passage?: string; editorSelectionHint?: ConsentEditorSelectionHint } | null {
  const fromAnswered = readPendingManuscriptDraftFromAssistantMetadata(answeredAssistant?.metadata);
  if (fromAnswered?.passage || fromAnswered?.editorSelectionHint) return fromAnswered;

  const list = messages ?? [];
  const answeredIdx = answeredAssistant
    ? list.findIndex((message) => message === answeredAssistant)
    : -1;
  const searchThrough = answeredIdx >= 0 ? list.slice(0, answeredIdx) : list;
  let lastUserIdx = -1;
  for (let i = searchThrough.length - 1; i >= 0; i -= 1) {
    if (searchThrough[i]?.role === "user") {
      lastUserIdx = i;
      break;
    }
  }
  const searchFrom = lastUserIdx >= 0 ? lastUserIdx + 1 : 0;
  for (let i = searchThrough.length - 1; i >= searchFrom; i -= 1) {
    const candidate = searchThrough[i];
    if (candidate?.role !== "assistant") continue;
    const replay = readPendingManuscriptDraftFromAssistantMetadata(candidate.metadata);
    if (replay?.passage || replay?.editorSelectionHint) return replay;
  }
  return null;
}

type LanguagePreferenceToastState =
  | { type: "hidden" }
  | { type: "prompt"; languageCode: string }
  | { type: "saved"; languageCode: string };

function getQuestionSignature(
  questions:
    | Array<{
        question?: string;
        inputType?: string;
        options?: Array<{ value?: string; label?: string }>;
      }>
    | null
    | undefined,
): string {
  if (!Array.isArray(questions) || questions.length === 0) return "";
  return questions
    // Match by structural prompt identity only; option payloads can vary slightly
    // between equivalent asks and should not bypass duplicate suppression.
    .map((q) => `${String(q.question ?? "")}::${String(q.inputType ?? "")}`)
    .join("||");
}

/** Avoids setPlan churn when message-derived plan matches store (prevents feedback loops if this effect ever lists activePlan in deps). */
function plansEquivalentForMessageSync(a: PlanData | null, b: PlanData): boolean {
  if (!a) return false;
  if (a.id !== b.id || a.status !== b.status || a.currentStepIndex !== b.currentStepIndex) return false;
  if (a.description !== b.description) return false;
  if ((a.specialConsiderations ?? "") !== (b.specialConsiderations ?? "")) return false;
  if (a.steps.length !== b.steps.length) return false;
  for (let i = 0; i < a.steps.length; i++) {
    const s = a.steps[i];
    const t = b.steps[i];
    if (String(s.id) !== String(t.id) || s.title !== t.title || s.status !== t.status) return false;
    if ((s.description ?? "") !== (t.description ?? "")) return false;
  }
  const creditA = a.estimatedCreditCost ?? a.estimates?.estimatedCreditCost;
  const creditB = b.estimatedCreditCost ?? b.estimates?.estimatedCreditCost;
  if (creditA !== creditB) return false;
  const rangeA = a.creditCostEstimateRange ?? a.estimates?.creditCostEstimateRange;
  const rangeB = b.creditCostEstimateRange ?? b.estimates?.creditCostEstimateRange;
  return creditRangesShallowEqual(rangeA, rangeB);
}

function numEqual(a: number | undefined, b: number | undefined): boolean {
  if (a === undefined && b === undefined) return true;
  if (a === undefined || b === undefined) return false;
  return a === b;
}

function creditRangesShallowEqual(
  a: { min?: number; max?: number; avg?: number } | undefined,
  b: { min?: number; max?: number; avg?: number } | undefined,
): boolean {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  return numEqual(a.min, b.min) && numEqual(a.max, b.max) && numEqual(a.avg, b.avg);
}

const getMessageRenderKey = (message: PersistentChatMessageData, index: number): string => {
  if (message.id) return `message-${message.id}`;
  const createdAt = message.created_at ? new Date(message.created_at).getTime() : "na";
  return `message-${message.role}-${createdAt}-${index}`;
};

// Height of ChatNavigation (h-[4rem]), which overlays the top of the cards container
const CHAT_NAV_HEIGHT_PX = 64;

/** Shown when opening agentic chat from the project Help menu (not persisted). */
const CHAT_HAS_OUTLINE_KEY = "@sudowrite/chat/has-outline";

const getCachedHasOutline = (projectId: string | undefined): boolean | undefined => {
  if (!projectId) return undefined;
  try {
    const cache = JSON.parse(localStorage.getItem(CHAT_HAS_OUTLINE_KEY) || '{}');
    return cache[projectId];
  } catch {
    return undefined;
  }
};

const setCachedHasOutline = (projectId: string | undefined, hasOutline: boolean) => {
  if (!projectId) return;
  try {
    const cache = JSON.parse(localStorage.getItem(CHAT_HAS_OUTLINE_KEY) || '{}');
    cache[projectId] = hasOutline;
    localStorage.setItem(CHAT_HAS_OUTLINE_KEY, JSON.stringify(cache));
  } catch {}
};

const StreamingMessage = memo(({
  persistedMessage,
  mode,
  suggestedAction,
  ctaAction,
  feedbackByMessageId,
  activeDocumentId,
  activeProjectId,
}: {
  persistedMessage: PersistentChatMessageData | undefined;
  mode: "write" | "chat";
  suggestedAction: any;
  ctaAction: any;
  feedbackByMessageId: Record<string, number> | undefined;
  activeDocumentId?: string | null;
  activeProjectId?: string | null;
}) => {
  const streaming = usePersistentChatStore((s) => s.streaming);
  const pendingAgentResponse = usePersistentChatStore((s) => s.pendingAgentResponse);
  const currentStreamingText = usePersistentChatStore((s) => s.currentStreamingText);
  const completedPlanSummary = usePersistentChatStore((s) => s.completedPlanSummary);
  const completedPlanSummaryId = usePersistentChatStore((s) => s.completedPlanSummaryId);
  const awaitingPlanSummary = usePersistentChatStore((s) => s.completedResponseAwaitingPersistence) && !!completedPlanSummary;
  const searchedDocs = usePersistentChatStore((s) => s.searchedDocs);
  const lookingAtData = usePersistentChatStore((s) => s.lookingAtData);
  const cutoffData = usePersistentChatStore((s) => s.cutoffData);
  const contextWarning = usePersistentChatStore((s) => s.contextWarning);
  const toolRuns = usePersistentChatStore((s) => s.toolRuns);
  const outlineUpdates = usePersistentChatStore((s) => s.outlineUpdates);
  const characterUpdates = usePersistentChatStore((s) => s.characterUpdates);
  const worldbuildingUpdates = usePersistentChatStore((s) => s.worldbuildingUpdates);
  const metaUpdates = usePersistentChatStore((s) => s.metaUpdates);
  const thinkingSteps = usePersistentChatStore((s) => s.thinkingSteps);
  const streamingContextElements = usePersistentChatStore((s) => s.contextElements);
  const streamingMentionedEntities = usePersistentChatStore((s) => s.mentionedEntities);
  const streamingModel = usePersistentChatStore((s) => s.currentModel);
  const streamingWritePovTenseChiclets = usePersistentChatStore((s) => s.currentWritePovTenseChiclets);
  const streamingCtaAction = usePersistentChatStore((s) => s.ctaAction);
  const streamingStoryBibleGeneratedEntity = usePersistentChatStore((s) => s.streamingStoryBibleGeneratedEntity);
  const useFloatingStoryBibleCtaProgress =
    streamingCtaAction?.status === "in_progress"
    && isStoryBibleCtaType(streamingCtaAction.type)
    && !streamingStoryBibleGeneratedEntity;
  const currentTurnUserMessageId = usePersistentChatStore((s) => s.currentTurnUserMessageId);
  const currentTurnPassage = usePersistentChatStore((s) => s.currentTurnPassage);
  const currentTurnReferencedComments = usePersistentChatStore((s) => s.currentTurnReferencedComments);

  const isAgentResponding = streaming || pendingAgentResponse;

  const persistedAssistantForCurrentTurn = persistedMessage?.role === "assistant"
    && (!awaitingPlanSummary || (persistedMessage.metadata?.planExecution === true
      && persistedMessage.metadata?.plan?.id === completedPlanSummaryId
      && persistedMessage.metadata?.plan?.status === "completed"))
    && (!currentTurnUserMessageId || persistedMessage.metadata?.triggerUserMessageId === currentTurnUserMessageId);

  const persistedStoryBibleEntity = persistedAssistantForCurrentTurn
    ? parseStoryBibleGeneratedEntity(
      (persistedMessage.metadata as { storyBibleGeneratedEntity?: unknown } | undefined)?.storyBibleGeneratedEntity,
    )
    : null;
  const persistedRewriteCarousel = persistedAssistantForCurrentTurn
    && (messageHasRewriteSuggestionCarouselMetadata(persistedMessage) || messageHasFinalManuscriptChange(persistedMessage));

  const showStoryBibleEntityHandoff =
    !!streamingStoryBibleGeneratedEntity && !persistedStoryBibleEntity;

  const showCompletedStoryBibleCtaHandoff =
    streamingCtaAction?.status === "completed"
    && isStoryBibleCtaType(streamingCtaAction.type)
    && !persistedStoryBibleEntity
    && !streamingStoryBibleGeneratedEntity;

  const currentProsePreview = usePersistentChatStore((s) => s.currentProsePreview);
  const showStreamingMessage =
    !!currentProsePreview
    || isAgentResponding
    || !!currentStreamingText
    || awaitingPlanSummary
    || showStoryBibleEntityHandoff
    || showCompletedStoryBibleCtaHandoff;
  if (!showStreamingMessage) return null;

  // The saved row owns this turn's live text and thinking once it exists.
  // Rendering the temporary row too would duplicate the same progress state.
  if (persistedAssistantForCurrentTurn && (isAgentResponding || currentProsePreview || persistedMessage.content?.trim() || persistedRewriteCarousel)) {
    return null;
  }

  if (!streaming && persistedAssistantForCurrentTurn && persistedStoryBibleEntity) {
    return null;
  }

  // Parent list patches empty persisted assistant rows with local text once `streaming` is false.
  // Use `streaming` here (not `isAgentResponding`): `pendingAgentResponse` can still be true while the
  // client holds recovered text, and the patched list row already owns rendering.
  if (
    !streaming
    && persistedAssistantForCurrentTurn
    && !persistedMessage.content?.trim()
    && !persistedRewriteCarousel
    && currentStreamingText?.trim()
  ) {
    return null;
  }

  const message: PersistentChatMessageData = {
    id: "tbd",
    role: "assistant" as const,
    content: currentStreamingText || (isAgentResponding || awaitingPlanSummary ? completedPlanSummary : ""),
    created_at: new Date(),
    metadata: {
      ...(currentTurnUserMessageId ? { triggerUserMessageId: currentTurnUserMessageId } : {}),
      ...(currentTurnPassage ? { passage: currentTurnPassage } : {}),
      ...(Array.isArray(currentTurnReferencedComments) && currentTurnReferencedComments.length > 0
        ? { referencedComments: currentTurnReferencedComments }
        : {}),
    },
    user_id: "",
    chat_id: "",
  };

  return (
    <div
      data-message
      data-role="assistant"
      className={cn(
        "py-1",
        (suggestedAction && !isAgentResponding) || (ctaAction && (ctaAction.status === "awaiting_input" || ctaAction.status === "cancelled"))
          ? "pb-1"
          : (!isAgentResponding && currentStreamingText)
            ? "pb-2"
            : "pb-10",
      )}
    >
      <PersistentChatMessage
        message={message}
        streaming={isAgentResponding}
        lookingAtData={lookingAtData}
        searchedDocs={searchedDocs}
        cutoffData={cutoffData}
        contextWarning={contextWarning}
        toolRuns={toolRuns}
        outlineUpdates={outlineUpdates}
        characterUpdates={characterUpdates}
        worldbuildingUpdates={worldbuildingUpdates}
        metaUpdates={metaUpdates}
        thinkingSteps={thinkingSteps}
        revertOption={undefined}
        isLastMessage={true}
        isLastAssistantMessage={true}
        streamingModel={streamingModel}
        streamingWritePovTenseChiclets={streamingWritePovTenseChiclets}
        streamingContextElements={streamingContextElements}
        streamingMentionedEntities={streamingMentionedEntities}
        mode={mode}
        initialRating={feedbackByMessageId?.[message.id] ?? null}
        ctaAction={
          streamingStoryBibleGeneratedEntity
            ? undefined
            : streamingCtaAction?.status === "completed"
              ? streamingCtaAction ?? undefined
              : useFloatingStoryBibleCtaProgress
                ? undefined
                : streamingCtaAction?.status === "in_progress"
                  ? streamingCtaAction ?? undefined
                  : undefined
        }
        onCtaGo={undefined}
        onCtaCancel={undefined}
        activeDocumentId={activeDocumentId}
        activeProjectId={activeProjectId}
      />
    </div>
  );
});

export const PersistentChatUI = () => {
  const {
    sendMessage,
    messages: _messages,
    branchRawMessages,
    chatId,
    setChatId,
    feedbackByMessageId,
    refetch,
    hasMoreOlderMessages,
    isLoadingOlderMessages,
    loadOlderMessages,
    isLoading,
    isFetching,
  } = usePersistentChat();

  const streaming = usePersistentChatStore((s) => s.streaming);
  const currentStreamingText = usePersistentChatStore((s) => s.currentStreamingText);
  const currentTurnUserMessageId = usePersistentChatStore((s) => s.currentTurnUserMessageId);
  const currentTurnPassage = usePersistentChatStore((s) => s.currentTurnPassage);
  const errorText = usePersistentChatStore((s) => s.errorText);
  const activeHighlight = usePersistentChatStore((s) => s.activeHighlight);
  const { dismissActiveHighlightFromComposer } = usePersistentChat();
  const activePlan = usePersistentChatStore((s) => s.activePlan);
  const activeCommenting = usePersistentChatStore((s) => s.activeCommenting);
  const cancelCommenting = usePersistentChatStore((s) => s.cancelCommenting);
  const pendingAgentResponse = usePersistentChatStore((s) => s.pendingAgentResponse);
  const completedResponseAwaitingPersistence = usePersistentChatStore((s) => s.completedResponseAwaitingPersistence);
  const completedPlanSummary = usePersistentChatStore((s) => s.completedPlanSummary);
  const completedPlanSummaryId = usePersistentChatStore((s) => s.completedPlanSummaryId);
  const reconcilingBeforeSend = usePersistentChatStore((s) => s.reconcilingBeforeSend);
  const failedFollowUp = usePersistentChatStore((s) => s.failedFollowUp);
  const activeTurnSearchedDocs = usePersistentChatStore((s) => s.searchedDocs);
  const activeTurnLookingAtData = usePersistentChatStore((s) => s.lookingAtData);
  const activeTurnCutoffData = usePersistentChatStore((s) => s.cutoffData);
  const activeTurnContextWarning = usePersistentChatStore((s) => s.contextWarning);
  const activeTurnToolRuns = usePersistentChatStore((s) => s.toolRuns);
  const activeTurnOutlineUpdates = usePersistentChatStore((s) => s.outlineUpdates);
  const activeTurnCharacterUpdates = usePersistentChatStore((s) => s.characterUpdates);
  const activeTurnWorldbuildingUpdates = usePersistentChatStore((s) => s.worldbuildingUpdates);
  const activeTurnMetaUpdates = usePersistentChatStore((s) => s.metaUpdates);
  const activeTurnThinkingSteps = usePersistentChatStore((s) => s.thinkingSteps);
  const activeTurnContextElements = usePersistentChatStore((s) => s.contextElements);
  const activeTurnMentionedEntities = usePersistentChatStore((s) => s.mentionedEntities);
  const activeTurnModel = usePersistentChatStore((s) => s.currentModel);
  const activeTurnWritePovTenseChiclets = usePersistentChatStore((s) => s.currentWritePovTenseChiclets);
  const commentingInProgress = isCommentingKeepingChatTurnOpen(activeCommenting);
  const currentRetryRequestId = usePersistentChatStore((s) => s.currentRetryRequestId);
  const currentRetryAssistantMessageId = usePersistentChatStore((s) => s.currentRetryAssistantMessageId);
  const stopping = usePersistentChatStore((s) => s.stopping);
  const frozenReplies = usePersistentChatStore((s) => s.frozenReplies);
  const queuedSend = usePersistentChatStore((s) => s.queuedSend);
  const agentIsRunning = streaming || pendingAgentResponse || reconcilingBeforeSend || (!stopping && commentingInProgress);
  const pendingNewChatMaterializationId = usePersistentChatStore((s) => s.pendingNewChatMaterializationId);
  const suggestedAction = usePersistentChatStore((s) => s.suggestedAction);
  const currentUserQuestion = usePersistentChatStore((s) => s.currentUserQuestion);
  const clearCurrentUserQuestion = usePersistentChatStore((s) => s.clearCurrentUserQuestion);
  const ctaAction = usePersistentChatStore((s) => s.ctaAction);
  const streamingStoryBibleGeneratedEntity = usePersistentChatStore((s) => s.streamingStoryBibleGeneratedEntity);
  const setCtaAction = usePersistentChatStore((s) => s.setCtaAction);
  const setPlan = usePersistentChatStore((s) => s.setPlan);
  const approvePlan = usePersistentChatStore((s) => s.approvePlan);
  const cancelPlan = usePersistentChatStore((s) => s.cancelPlan);
  const pausePlan = usePersistentChatStore((s) => s.pausePlan);
  const resumePlan = usePersistentChatStore((s) => s.resumePlan);
  const planResumeRequestInFlight = usePersistentChatStore((s) => s.planResumeRequestInFlight);
  const pausePlanApiRequestInFlight = usePersistentChatStore((s) => s.pausePlanApiRequestInFlight);
  const { userSettings, updateUserSettings } = usePersistentChatUserSettings();
  const {
    agenticChatMode,
    modeLabel,
    modeLabelVariant,
    isAdmin,
    agenticChatBrainModel,
    adminBrainModelOptions,
  } = userSettings;
  const composerModeLabels = useMemo(
    () => getAgenticChatModeLabels(modeLabelVariant),
    [modeLabelVariant],
  );
  const getModelKeyRef = useRef(userSettings.getModelKey);
  getModelKeyRef.current = userSettings.getModelKey;
  const updateUserSettingsRef = useRef(updateUserSettings);
  updateUserSettingsRef.current = updateUserSettings;
  const [inlineEditUserMessageId, setInlineEditUserMessageId] = useState<string | null>(null);
  const [submittedAnswer, setSubmittedAnswer] = useState<
    (AppendedDocumentMentionsResult & { questionMessageId: string }) | null
  >(null);
  const answeredQuestionMessageIdsRef = useRef<Set<string>>(new Set());
  // Prevent stale question rehydration from reopening the exact question that was just answered.
  const suppressRestoreQuestionMessageIdRef = useRef<string | null>(null);
  const suppressRestoreQuestionSignatureRef = useRef<string | null>(null);
  const suppressFeedbackTypesPickerRef = useRef(false);
  const questionTransitionLockTimeoutRef = useRef<number | null>(null);
  const [questionTransitionLocked, setQuestionTransitionLocked] = useState(false);
  // Track which chat the current question belongs to, and store questions for chats that have unanswered questions
  const questionChatIdRef = useRef<string | null>(null);
  const unansweredQuestionsByChatRef = useRef<Map<string, NonNullable<typeof currentUserQuestion>>>(new Map());
  const project = useProjectStore((state) => state.project);
  const projectId = project?.id ?? "";
  const contents = useProjectStore((state) => state.contents);
  const gatedImportReportDocumentId = gateImportReportDocumentId(
    project?.importReportDocumentId ?? null,
    contents,
  );
  const activeDocumentId = useProjectStore((state) => state.document?.id) ?? null;
  const queryClient = useQueryClient();
  const { setToast } = useToast();
  const switchBranchMutation = useSetActiveBranch(projectId, () => {
    setToast("Could not switch branch. Please try again.");
  });
  const { memories, addMemory, updateMemory } = useMemories();
  const [inlineToast, setInlineToast] = useState<string | null>(null);
  const [composerAttachmentDocs, setComposerAttachmentDocs] = useState<AttachmentDoc[]>([]);
  const [showModeSwitchBackToast, setShowModeSwitchBackToast] = useState(false);
  const prevComposerModeRef = useRef(userSettings.agenticChatMode);
  const [languagePreferenceToast, setLanguagePreferenceToast] = useState<LanguagePreferenceToastState>({ type: "hidden" });
  const [languagePreferenceSaving, setLanguagePreferenceSaving] = useState(false);
  const [revertedAssistantMessageIds, setRevertedAssistantMessageIds] = useState<Set<string>>(() => new Set());
  const dismissedLanguagePreferenceCodesRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    dismissedLanguagePreferenceCodesRef.current.clear();
    setLanguagePreferenceToast((prev) => (prev.type === "hidden" ? prev : { type: "hidden" }));
    setLanguagePreferenceSaving((s) => (s ? false : s));
    setInlineEditUserMessageId(null);
    setComposerAttachmentDocs((prev) => (prev.length === 0 ? prev : []));
    setRevertedAssistantMessageIds((prev) => (prev.size === 0 ? prev : new Set()));
  }, [chatId]);

  const handleRevertStoryBibleSuccess = useCallback(
    (assistantMessageId: string) => {
      if (!assistantMessageId) return;
      setRevertedAssistantMessageIds((prev) => {
        if (prev.has(assistantMessageId)) return prev;
        const next = new Set(prev);
        next.add(assistantMessageId);
        return next;
      });
      void Auth.fetchWithIdToken("/api/persistentChat/message/update-metadata", {
        method: "POST",
        chatId,
        messageId: assistantMessageId,
        metadataPatch: {
          storyBibleRevertApplied: true,
        },
      }).catch(() => {
        // Best effort persist; local state still reflects the successful revert in this session.
      });
    },
    [chatId],
  );

  const subscriptionJSON = useSudowriteStore((state) => state.subscriptionJSON);
  const trialFreeChatSubscriptionView = useMemo(
    () => subscriptionViewFromClientJSON(subscriptionJSON),
    [subscriptionJSON],
  );
  const discussModeAvailable =
    !subscriptionJSON?.activeTrial || !isTrialDiscussModeBlocked(trialFreeChatSubscriptionView);
  const outline = useStructuredOutlineStore((state) => state.outline);
  const hasOutlineItems = outline?.content?.items?.length > 0;
  const cachedHasOutline = useMemo(() => getCachedHasOutline(projectId), [projectId]);
  const isNewProject = hasOutlineItems ? false : cachedHasOutline === true ? false : true;
  const isFeedbackRunning = useCrossAppSignalStore((state) => state.signals?.feedbackRunning ?? false);
  const feedbackRunSeenActiveRef = useRef(false);

  useEffect(() => {
    if (hasOutlineItems) {
      setCachedHasOutline(projectId, true);
      return;
    }
    const outlineId = typeof (outline as any)?.id === "string" ? (outline as any).id.trim() : "";
    // If the loaded outline object exists but has no items, clear stale "has outline" cache.
    if (outlineId) {
      setCachedHasOutline(projectId, false);
    }
  }, [hasOutlineItems, projectId, outline]);

  const inputBoxRef = useRef<HTMLDivElement>(null);
  const [inputHeight, setInputHeight] = useState(0);
  const cardsContainerRef = useRef<HTMLDivElement>(null);
  const [composerCoversNav, setComposerCoversNav] = useState(false);
  const [isComposerModeMenuOpen, setIsComposerModeMenuOpen] = useState(false);
  const planCardRef = useRef<HTMLDivElement>(null);
  const [planCardHeight, setPlanCardHeight] = useState(0);
  const commentingCardRef = useRef<HTMLDivElement>(null);
  const [commentingCardHeight, setCommentingCardHeight] = useState(0);
  const storyBibleCtaCardRef = useRef<HTMLDivElement>(null);
  const [storyBibleCtaCardHeight, setStoryBibleCtaCardHeight] = useState(0);
  const chatExperienceSurveyRef = useRef<HTMLDivElement>(null);
  const [chatExperienceSurveyHeight, setChatExperienceSurveyHeight] = useState(0);
  const [isPlanEditing, setIsPlanEditing] = useState(false);
  const [messagesTopOffset, setMessagesTopOffset] = useState(0);

  const composerRef = useRef<ChatComposerHandle>(null);
  const activeHighlightDismissButtonRef = useRef<HTMLButtonElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const spacerRef = useRef<HTMLDivElement>(null);
  const previousChatIdRef = useRef<string | null>(null);
  const previousProjectIdRef = useRef<string | null>(null);
  /** After selecting a chat, scroll to the latest message once its messages are in the DOM. */
  const pendingScrollToBottomChatIdRef = useRef<string | null>(null);
  const sentThisSessionRef = useRef(false);
  const savedToolRunsCache = useRef(new Map<string, AgenticToolRun[]>());
  const [isAtBottom, setIsAtBottom] = useState(true);
  const checkIsAtBottom = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const msgs = container.querySelectorAll("[data-message]");
    const lastMsg = msgs[msgs.length - 1] as HTMLElement | null;
    if (lastMsg) {
      const containerRect = container.getBoundingClientRect();
      const lastMsgRect = lastMsg.getBoundingClientRect();
      const atBottom = lastMsgRect.bottom <= containerRect.bottom + 50;
      setIsAtBottom((prev) => (prev === atBottom ? prev : atBottom));
    } else {
      setIsAtBottom(true);
    }
  }, []);
  const [chatViewHeight, setChatViewHeight] = useState(0);
  const updateMessagesTopOffset = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    setMessagesTopOffset((prev) => {
      const next = container.offsetTop;
      return prev === next ? prev : next;
    });
  }, []);
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const ro = new ResizeObserver(([e]) => {
      setChatViewHeight(e.contentRect.height);
      updateMessagesTopOffset();
      checkIsAtBottom();
    });
    ro.observe(container);
    updateMessagesTopOffset();
    return () => ro.disconnect();
  }, [checkIsAtBottom, updateMessagesTopOffset, (_messages?.length ?? 0) > 0]);

  useEffect(() => {
    updateMessagesTopOffset();
    if (!isPlanEditing) return;
    const recalcTopOffset = () => updateMessagesTopOffset();
    let raf = 0;
    const onLayout = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        recalcTopOffset();
      });
    };
    onLayout();
    window.addEventListener("resize", onLayout);
    window.addEventListener("scroll", onLayout, true);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("resize", onLayout);
      window.removeEventListener("scroll", onLayout, true);
    };
  }, [isPlanEditing, updateMessagesTopOffset]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    const content = contentRef.current;
    const spacer = spacerRef.current;
    if (!container || !content || !spacer || chatViewHeight === 0) return;

    const updateSpacer = () => {
      if (!sentThisSessionRef.current) {
        spacer.style.height = "0px";
        checkIsAtBottom();
        return;
      }
      const userMsgs = container.querySelectorAll('[data-role="user"]');
      const lastUserMsg = userMsgs[userMsgs.length - 1] as HTMLElement | null;
      if (!lastUserMsg) {
        spacer.style.height = "0px";
        checkIsAtBottom();
        return;
      }
      const contentRect = content.getBoundingClientRect();
      const userMsgRect = lastUserMsg.getBoundingClientRect();
      const contentBelowUserMsg = contentRect.bottom - userMsgRect.top;
      spacer.style.height = `${Math.max(0, chatViewHeight - contentBelowUserMsg)}px`;
      checkIsAtBottom();
    };

    const ro = new ResizeObserver(updateSpacer);
    ro.observe(content);
    updateSpacer();
    return () => ro.disconnect();
  }, [chatViewHeight, checkIsAtBottom, (_messages?.length ?? 0) > 0]);

  const loadOlderMessagesRef = useRef(loadOlderMessages);
  loadOlderMessagesRef.current = loadOlderMessages;
  const isLoadingOlderMessagesRef = useRef(isLoadingOlderMessages);
  isLoadingOlderMessagesRef.current = isLoadingOlderMessages;
  const loadOlderInFlightRef = useRef(false);
  const loadOlderSentinelRef = useRef<HTMLDivElement>(null);

  const adjustScrollAfterPrepend = useCallback((prevScrollHeight: number, prevScrollTop: number) => {
    return new Promise<void>((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const nextContainer = messagesContainerRef.current;
          if (nextContainer) {
            nextContainer.scrollTop = prevScrollTop + (nextContainer.scrollHeight - prevScrollHeight);
          }
          resolve();
        });
      });
    });
  }, []);

  const loadOlderMessagesWithScrollPreserve = useCallback(async () => {
    if (loadOlderInFlightRef.current || isLoadingOlderMessagesRef.current) return;
    loadOlderInFlightRef.current = true;
    try {
      const container = messagesContainerRef.current;
      const prevScrollHeight = container?.scrollHeight ?? 0;
      const prevScrollTop = container?.scrollTop ?? 0;
      await loadOlderMessagesRef.current();
      if (container) {
        await adjustScrollAfterPrepend(prevScrollHeight, prevScrollTop);
      }
    } finally {
      loadOlderInFlightRef.current = false;
    }
  }, [adjustScrollAfterPrepend]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    let rafId: number | null = null;
    const handleScroll = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        checkIsAtBottom();
      });
    };
    handleScroll();
    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      container.removeEventListener("scroll", handleScroll);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [checkIsAtBottom, (_messages?.length ?? 0) > 0]);

  useEffect(() => {
    const sentinel = loadOlderSentinelRef.current;
    const root = messagesContainerRef.current;
    if (!sentinel || !root || !hasMoreOlderMessages) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting || loadOlderInFlightRef.current || isLoadingOlderMessagesRef.current) {
          return;
        }
        void loadOlderMessagesWithScrollPreserve();
      },
      { root, rootMargin: "200px 0px 0px 0px", threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMoreOlderMessages, loadOlderMessagesWithScrollPreserve, _messages?.length ?? 0]);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = "smooth") => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const top = el.scrollHeight;
    if (typeof el.scrollTo === "function") {
      el.scrollTo({ top, behavior });
    } else {
      el.scrollTop = top;
    }
  }, []);

  const scrollToNewMessage = useCallback((behavior: ScrollBehavior = "smooth") => {
    const container = messagesContainerRef.current;
    if (!container) return;

    scrollToLastMessage(container, behavior);
  }, []);

  useOnSudoLayoutEvent("focus_chat_input", () => {
    const ctaActive = usePersistentChatStore.getState().ctaAction?.status === "awaiting_input";
    const questionOpen = Boolean(usePersistentChatStore.getState().currentUserQuestion);
    if (composerRef.current && !ctaActive && !questionOpen) {
      setTimeout(() => {
        if (usePersistentChatStore.getState().currentUserQuestion) return;
        composerRef.current?.focus();
      }, 200);
    }
  });

  useEffect(() => {
    const previousProjectId = previousProjectIdRef.current;
    const projectChanged = previousProjectId !== null && previousProjectId !== projectId;
    previousProjectIdRef.current = projectId;

    const chatChanged = chatId !== previousChatIdRef.current;
    if (!projectChanged && !chatChanged) return;

    const previousChatId = previousChatIdRef.current;
    previousChatIdRef.current = chatId;
    sentThisSessionRef.current = false;
    savedToolRunsCache.current.clear();
    loadOlderInFlightRef.current = false;

    if ((projectChanged || chatChanged) && chatId && chatId !== NEW_CHAT_ID) {
      pendingScrollToBottomChatIdRef.current = chatId;
    } else {
      pendingScrollToBottomChatIdRef.current = null;
    }
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = 0;
    }

    // Save the current question for the previous chat (if there is one).
    // Read directly from the store to avoid re-running this effect on every question change.
    const storeAtChatSwitch = usePersistentChatStore.getState();
    const latestUserQuestion = storeAtChatSwitch.currentUserQuestion;
    const isExpectedMaterialization =
      !projectChanged
      && chatChanged
      && chatId != null
      && isExpectedNewChatMaterialization(
        previousChatId,
        chatId,
        storeAtChatSwitch.pendingNewChatMaterializationId,
      );
    const stashed = stashSnapshotForUnansweredQuestion(previousChatId, questionChatIdRef.current, latestUserQuestion);
    if (stashed) {
      unansweredQuestionsByChatRef.current.set(stashed.previousChatId, stashed.question);
    }

    if (projectChanged) {
      // Project switches should drop any in-flight stream and transient UI state from the old project.
      usePersistentChatStore.getState().reset();
      unansweredQuestionsByChatRef.current.clear();
    } else if (chatChanged && chatId != null) {
      const {
        ctaAction: latestCtaAction,
        pendingNewChatMaterializationId: latestPendingId,
        streaming: latestStreaming,
        pendingAgentResponse: latestPendingAgentResponse,
        responsePhase: latestResponsePhase,
      } = usePersistentChatStore.getState();
      const shouldReset = shouldResetPersistentChatOnSameProjectChatChange(
        previousChatId,
        chatId,
        latestCtaAction?.status,
        latestPendingId,
      );
      const initialMountForCurrentChat = previousChatId === null;
      const hasInFlightAgentResponse = latestStreaming || latestPendingAgentResponse || latestResponsePhase !== "idle";
      if (shouldReset && !(initialMountForCurrentChat && hasInFlightAgentResponse)) {
        usePersistentChatStore.getState().reset();
      }
      // Keep pending id through NEW_CHAT_ID → persisted id so cast-size follow-up sends stay on the CTA thread.
      if (latestPendingId && !isExpectedMaterialization) {
        usePersistentChatStore.setState({ pendingNewChatMaterializationId: null });
      }
    }

    // Clear the current question when switching chats/projects (not placeholder → persisted id on same CTA thread).
    if (!isExpectedMaterialization) {
      clearCurrentUserQuestion();
      questionChatIdRef.current = null;
    }
    answeredQuestionMessageIdsRef.current.clear();
    if (!projectChanged && chatId) {
      getDismissedUserQuestionMessageIds(projectId, chatId).forEach((messageId) => {
        answeredQuestionMessageIdsRef.current.add(messageId);
      });
    }
    suppressRestoreQuestionMessageIdRef.current = null;
    suppressRestoreQuestionSignatureRef.current = null;
    suppressFeedbackTypesPickerRef.current = false;
    if (questionTransitionLockTimeoutRef.current !== null) {
      clearTimeout(questionTransitionLockTimeoutRef.current);
      questionTransitionLockTimeoutRef.current = null;
    }
    setQuestionTransitionLocked(false);

    // Restore the question only when staying within the same project.
    if (!projectChanged && chatId) {
      const savedQuestion = unansweredQuestionsByChatRef.current.get(chatId);
      if (savedQuestion) {
        usePersistentChatStore.setState({ currentUserQuestion: savedQuestion });
        questionChatIdRef.current = chatId;
        unansweredQuestionsByChatRef.current.delete(chatId);
      }
    }

    setTimeout(() => {
      if (usePersistentChatStore.getState().currentUserQuestion) return;
      composerRef.current?.focus();
    }, 200);
  }, [projectId, chatId, clearCurrentUserQuestion]);

  useEffect(() => {
    if (pendingScrollToBottomChatIdRef.current !== chatId || !chatId || chatId === NEW_CHAT_ID) return;
    if ((isLoading || isFetching) && !(_messages?.length ?? 0)) return;
    if (!(_messages?.length ?? 0)) return;

    let cancelled = false;
    const scrollToLatest = () => {
      if (cancelled || pendingScrollToBottomChatIdRef.current !== chatId) return;
      const container = messagesContainerRef.current;
      if (!container) return;
      scrollToBottom("instant");
    };

    scrollToLatest();
    const outerRaf = requestAnimationFrame(() => {
      const innerRaf = requestAnimationFrame(() => {
        scrollToLatest();
        if (!cancelled && pendingScrollToBottomChatIdRef.current === chatId) {
          pendingScrollToBottomChatIdRef.current = null;
        }
      });
      if (cancelled) cancelAnimationFrame(innerRaf);
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(outerRaf);
    };
  }, [chatId, isLoading, isFetching, _messages?.length, scrollToBottom]);

  const handleComposerKeyDownCapture = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (!(event.shiftKey && event.key === "Tab" && activeHighlight?.trim())) return;
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      const isEditorTarget = target.isContentEditable || !!target.closest("[contenteditable='true']");
      if (!isEditorTarget) return;
      const closeButton = activeHighlightDismissButtonRef.current;
      if (!closeButton) return;
      event.preventDefault();
      closeButton.focus();
    },
    [activeHighlight],
  );

  // Background tabs can finish a plan stream before chat refetch lands; re-pull persisted plan metadata when the tab is visible again.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      const { activePlan: livePlan, streaming: isStreaming, pendingAgentResponse: pending, planResumeRequestInFlight: resumeInFlight } =
        usePersistentChatStore.getState();
      if (!livePlan || isStreaming || pending || resumeInFlight) return;
      if (livePlan.status !== "paused" && livePlan.status !== "in_progress") return;
      void refetch();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [refetch]);

  useEffect(() => {
    if (!_messages?.length || streaming) return;
    const storePlan = () => usePersistentChatStore.getState().activePlan;
    const activePlanNow = storePlan();
    const plan = resolvePersistedPlanForSync(_messages, activePlanNow?.id);
    if (plan && (plan.status === "proposed" || plan.status === "in_progress" || plan.status === "paused")) {
      // While we have a live (non-terminal) plan in the store, do not let a *proposed* snapshot
      // in message metadata (same or other plan id) overwrite it — refetches are often stale.
      // Terminal (completed/cancelled) local plans are excluded so a new proposed plan can sync.
      const shouldPreserveActivePlan =
        !!activePlanNow
        && (activePlanNow.status === "in_progress" || activePlanNow.status === "paused" || activePlanNow.status === "cancelling")
        && plan.status === "proposed";

      if (shouldPreserveActivePlan) {
        return;
      }

      // If an incoming in_progress plan arrives while nothing is actively running,
      // treat it as interrupted and show paused. But keep it in_progress when this
      // is the currently active local plan during/after resume.
      let effectivePlan = plan;
      if (plan.status === "in_progress" && !streaming && !pendingAgentResponse && !planResumeRequestInFlight) {
        const isSameActiveInProgressPlan =
          !!activePlanNow
          && activePlanNow.id === plan.id
          && activePlanNow.status === "in_progress";
        if (!isSameActiveInProgressPlan) {
          effectivePlan = { ...plan, status: "paused" as const };
        }
      }

      // Never regress local progress for an already active plan with the same ID.
      if (activePlanNow && activePlanNow.id === effectivePlan.id && activePlanNow.status !== "proposed" && effectivePlan.status !== "proposed") {
        // Higher = further along; cancelled is terminal (not comparable to in_progress the same as completed).
        const statusRank = (s: PlanData["steps"][number]["status"]): number => {
          switch (s) {
            case "pending": return 0;
            case "in_progress": return 1;
            case "cancelled": return 2;
            case "completed": return 3;
            default: return 0;
          }
        };
        const activeStepById = new Map<string, PlanData["steps"][number]>(activePlanNow.steps.map((step) => [step.id, step]));
        const mergedSteps = effectivePlan.steps.map((incomingStep: PlanData["steps"][number]) => {
          const activeStep = activeStepById.get(incomingStep.id);
          if (!activeStep) return incomingStep;
          // Refetched "pending" should win over a local per-step "cancelled" (user re-enabled the step).
          if (activeStep.status === "cancelled" && incomingStep.status === "pending") {
            return incomingStep;
          }
          // Local cancelled should only beat an incoming in_progress (e.g. stale mid-step chunk),
          // not a fresh pending from refetched plan metadata.
          if (activeStep.status === "cancelled" && incomingStep.status === "in_progress") {
            return { ...incomingStep, status: "cancelled" };
          }
          if (activeStep.status === "in_progress" && incomingStep.status === "cancelled") {
            return { ...incomingStep, status: "in_progress" };
          }
          return statusRank(activeStep.status) > statusRank(incomingStep.status)
            ? { ...incomingStep, status: activeStep.status }
            : incomingStep;
        });
        effectivePlan = {
          ...effectivePlan,
          steps: mergedSteps,
          currentStepIndex: resolvePlanCurrentStepIndex({ ...effectivePlan, steps: mergedSteps }),
        };
      }
      if (
        activePlanNow
        && activePlanNow.id === effectivePlan.id
        && activePlanNow.status === "in_progress"
        && effectivePlan.status === "paused"
        && planResumeRequestInFlight
      ) {
        effectivePlan = { ...effectivePlan, status: "in_progress" as const };
      }
      // Never let stale message metadata regress an already-paused local plan back to in_progress.
      if (
        activePlanNow
        && activePlanNow.id === effectivePlan.id
        && activePlanNow.status === "paused"
        && effectivePlan.status === "in_progress"
      ) {
        effectivePlan = { ...effectivePlan, status: "paused" as const };
      }
      const normalized = normalizeTerminalPlanStatus(effectivePlan);
      const current = storePlan();
      if (!plansEquivalentForMessageSync(current, normalized)) {
        if (normalized.status === "completed" || normalized.status === "cancelled") {
          setPlan(null);
        } else {
          setPlan(normalized);
        }
      }
      return;
    }

    const latestAssistantPlan = plan;
    const latestAssistantPlanIsTerminal =
      latestAssistantPlan?.status === "completed"
      || latestAssistantPlan?.status === "cancelled";

    const activeForRunning = storePlan();
    const authoritativeTerminalForActive =
      activeForRunning?.id
        ? [..._messages].reverse().find((m) => {
          if (m.role !== "assistant" || !m.metadata?.plan) return false;
          const p = m.metadata.plan;
          return p.id === activeForRunning.id && (p.status === "completed" || p.status === "cancelled");
        })
        : undefined;
    const shouldKeepRunningPlan =
      activeForRunning?.status === "proposed"
      || activeForRunning?.status === "in_progress"
      || activeForRunning?.status === "cancelling"
      || activeForRunning?.status === "paused";

    if (
      authoritativeTerminalForActive
      && activeForRunning !== null
      && (activeForRunning.status === "in_progress"
        || activeForRunning.status === "paused"
        || activeForRunning.status === "cancelling"
        || activeForRunning.status === "proposed")
    ) {
      setPlan(null);
      return;
    }

    if ((!latestAssistantPlan || latestAssistantPlanIsTerminal) && !shouldKeepRunningPlan && storePlan() !== null) {
      setPlan(null);
    }
  }, [chatId, _messages, streaming, pendingAgentResponse, planResumeRequestInFlight, setPlan]);

  // Restore unanswered question buttons on chat reload.
  // While a follow-up turn is pending, keep the picker hidden to avoid briefly
  // resurrecting the just-answered question before the next ask_user_question arrives.
  useEffect(() => {
    if (!_messages?.length || agentIsRunning) return;
    collectAnsweredQuestionMessageIdsFromHistory(_messages).forEach((messageId) => {
      answeredQuestionMessageIdsRef.current.add(messageId);
    });
    const liveUserQuestion = usePersistentChatStore.getState().currentUserQuestion;
    // Hidden answers and CTA turns may not have an optimistic user row. Until this
    // completed turn reconciles, cached questions belong to the previous turn.
    if (completedResponseAwaitingPersistence && !errorText && ctaAction?.status !== "cancelled") return;
    const lastMessage = _messages[_messages.length - 1];
    const lastNonHidden = [..._messages].reverse().find((m) => !m.metadata?.hiddenFromUI);
    const messageToRestore = resolvePendingQuestionMessageToRestore(_messages);
    const suppressedQuestionId = suppressRestoreQuestionMessageIdRef.current;
    if (
      suppressedQuestionId
      && lastNonHidden?.id
      && lastNonHidden.id !== suppressedQuestionId
    ) {
      suppressRestoreQuestionMessageIdRef.current = null;
    }
    // After answering a question, the latest persisted assistant turn can still be that old
    // question for a moment. Don't re-open it while waiting for the next turn/question.
    if (
      suppressedQuestionId
      && lastNonHidden?.role === "assistant"
      && lastNonHidden.id === suppressedQuestionId
    ) {
      return;
    }
    if (lastMessage?.role === "user" && lastMessage.metadata?.hiddenFromUI && lastNonHidden?.role === "assistant" && lastNonHidden.metadata?.questionAsked && lastNonHidden.metadata?.userQuestion) {
      return;
    }
    if (messageToRestore?.metadata?.questionAsked && messageToRestore.metadata?.userQuestion) {
      const uq = messageToRestore.metadata.userQuestion;
      // Support both new format ({ questions }) and old format ({ question, inputType, options })
      const questions = uq.questions ?? (uq.question ? [{ question: uq.question, inputType: uq.inputType ?? "button", options: uq.options ?? [] }] : null);
      const questionSignature = getQuestionSignature(questions ?? undefined);
      const restoreMessageId = messageToRestore.id;
      if (
        (restoreMessageId
          && (answeredQuestionMessageIdsRef.current.has(restoreMessageId)
            || isAssistantQuestionAnsweredInMessages(_messages, restoreMessageId)))
        || isDismissedUserQuestion(projectId, chatId ?? undefined, restoreMessageId)
        || isDismissedUserQuestionSignature(projectId, chatId ?? undefined, questionSignature)
      ) {
        clearCurrentUserQuestion();
        return;
      }
      if (!questions?.length) {
        clearCurrentUserQuestion();
        return;
      }
      const nextQuestion = { questions };
      const nextSignature = getQuestionSignature(questions);
      const liveSignature = getQuestionSignature(liveUserQuestion?.questions);
      if (nextSignature !== liveSignature) {
        usePersistentChatStore.setState({
          currentUserQuestion: nextQuestion,
        });
      }
    } else if (lastMessage?.role === "assistant" && lastMessage.metadata?.questionAsked && liveUserQuestion) {
      // Keep the live streamed question visible when persisted metadata hasn't attached userQuestion yet.
      return;
    } else if (liveUserQuestion && lastMessage?.role === "user") {
      if (
        shouldKeepLiveQuestionWithUserMessageTail(_messages, {
          currentTurnUserMessageId,
          ctaCancelled: ctaAction?.status === "cancelled",
          hasStreamError: Boolean(errorText),
        })
      ) {
        return;
      }
      if (lastMessage.metadata?.hiddenFromUI) {
        return;
      }
      const lastVisibleAssistant = [..._messages].reverse().find(
        (message) => message.role === "assistant" && !message.metadata?.hiddenFromUI,
      );
      if (
        lastVisibleAssistant?.metadata?.questionAsked
        && !lastVisibleAssistant.metadata?.userQuestion
      ) {
        return;
      }
      clearCurrentUserQuestion();
    } else {
      clearCurrentUserQuestion();
    }
  }, [chatId, _messages, agentIsRunning, submittedAnswer, clearCurrentUserQuestion, projectId, ctaAction?.status, errorText, currentUserQuestion, currentTurnUserMessageId, completedResponseAwaitingPersistence]);

  useEffect(() => {
    if (!inlineToast) return;
    const timeoutId = setTimeout(() => setInlineToast(null), 3000);
    return () => clearTimeout(timeoutId);
  }, [inlineToast]);

  useEffect(() => {
    if (!peekAutoSwitchedFromChatToWrite()) return;
    if (userSettings.agenticChatMode !== "write") return;

    const ctaInFlight =
      ctaAction?.status === "awaiting_input" || ctaAction?.status === "in_progress";
    const workFinished =
      ctaAction?.status === "completed"
      || (!ctaInFlight && !streaming && !pendingAgentResponse);

    if (!workFinished) return;

    consumeAutoSwitchedFromChatToWrite();
    clearStoryBibleCtaAutoPromotedFromChat();
    if (discussModeAvailable) {
      setShowModeSwitchBackToast(true);
    }
  }, [ctaAction?.status, discussModeAvailable, userSettings.agenticChatMode, streaming, pendingAgentResponse]);

  useEffect(() => {
    if (ctaAction?.status === "in_progress" || ctaAction?.status === "completed") {
      clearStoryBibleCtaAutoPromotedFromChat();
    }
  }, [ctaAction?.status]);

  useEffect(() => {
    const prevMode = prevComposerModeRef.current;
    const nextMode = userSettings.agenticChatMode;
    prevComposerModeRef.current = nextMode;
    // Only clear on an explicit write → chat transition. userData.update from Story Bible
    // CTA promotion can lag behind React settings; clearing whenever mode !== "write"
    // wiped the switch-back flag before the toast could appear.
    if (prevMode === "write" && nextMode === "chat") {
      clearAutoSwitchedFromChatToWrite();
      clearStoryBibleCtaAutoPromotedFromChat();
      setShowModeSwitchBackToast(false);
    }
  }, [userSettings.agenticChatMode]);

  // Map assistant message IDs to revert options (includes the triggering user message info)
  const revertOptionsByAssistantMessageId = useMemo(() => {
    const map = new Map<
      string,
      {
        snapshotId?: string;
        outlineId?: string;
        projectId?: string;
        chatId: string;
        userMessageId: string;
        userMessageContent: string;
        snapshotBundle?: {
          outlineSnapshotId: string | null;
          outlineId: string | null;
          characterSnapshots: Record<string, string>;
          worldbuildingSnapshots: Record<string, string>;
          metaSnapshot: any;
        };
      }
    >();

    // Build a map of user message IDs to their content
    const userMessageMap = new Map<string, string>();
    (_messages || []).forEach((msg) => {
      if (msg.role === "user") {
        userMessageMap.set(msg.id, msg.content || "");
      }
    });

    (_messages || []).forEach((msg) => {
      if (msg.role !== "assistant") return;
      const metadata = msg.metadata || {};
      const triggerUserMessageId = metadata?.triggerUserMessageId;
      const snapshotBundle = metadata?.snapshotBundle;
      const snapshotId = snapshotBundle?.outlineSnapshotId || metadata?.outlineSnapshotBeforeUpdateId || undefined;
      const outlineId = snapshotBundle?.outlineId || metadata?.outlineId || undefined;

      if (triggerUserMessageId && userMessageMap.has(triggerUserMessageId)) {
        map.set(msg.id, {
          snapshotId,
          outlineId,
          projectId: metadata?.projectId || projectId,
          chatId,
          userMessageId: triggerUserMessageId,
          userMessageContent: userMessageMap.get(triggerUserMessageId) || "",
          snapshotBundle: snapshotBundle || undefined,
        });
      }
    });

    return map;
  }, [_messages, projectId, chatId]);

  useEffect(() => {
    if (!inputBoxRef.current) return;

    const observer = new ResizeObserver((v) => {
      if (!v[0]) return;
      // Use borderBoxSize if available for more accurate outer height, fallback to contentRect
      const height = v[0].borderBoxSize?.[0]?.blockSize ?? v[0].contentRect.height;
      setInputHeight(height);
    });

    observer.observe(inputBoxRef.current);
    return () => {
      observer.disconnect();
    };
  }, []);

  // When the software keyboard shrinks the panel (e.g. iPad landscape), the bottom-anchored composer
  // slides up under the absolutely positioned ChatNavigation header (z-50) and hides the text being
  // typed. Lift this container above the header only while they overlap (SW-10375).
  useEffect(() => {
    if (!document.documentElement.classList.contains("ios-viewport")) return;
    const container = cardsContainerRef.current;
    if (!container) return;

    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      const height = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;
      setComposerCoversNav(height > 0 && height - inputHeight < CHAT_NAV_HEIGHT_PX);
    });

    observer.observe(container);
    return () => {
      observer.disconnect();
    };
  }, [inputHeight]);

  useEffect(() => {
    if (!planCardRef.current) {
      setPlanCardHeight(0);
      return;
    }
    const observer = new ResizeObserver((v) => {
      if (!v[0]) return;
      const height = v[0].borderBoxSize?.[0]?.blockSize ?? v[0].contentRect.height;
      setPlanCardHeight(height);
    });
    observer.observe(planCardRef.current);
    return () => {
      observer.disconnect();
    };
  }, [activePlan?.status]);

  useEffect(() => {
    if (!activeCommenting || activeCommenting.status !== "in_progress") {
      setCommentingCardHeight(0);
      return;
    }
    const el = commentingCardRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      if (!entries[0]) return;
      const height = entries[0].borderBoxSize?.[0]?.blockSize ?? entries[0].contentRect.height;
      setCommentingCardHeight(height > 0 ? height + 8 : 0);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [activeCommenting?.status]);

  useEffect(() => {
    if (
      !ctaAction
      || ctaAction.status !== "in_progress"
      || !isStoryBibleCtaType(ctaAction.type)
      || streamingStoryBibleGeneratedEntity
    ) {
      setStoryBibleCtaCardHeight(0);
      return;
    }
    const el = storyBibleCtaCardRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      if (!entries[0]) return;
      const height = entries[0].borderBoxSize?.[0]?.blockSize ?? entries[0].contentRect.height;
      setStoryBibleCtaCardHeight(height > 0 ? height + 8 : 0);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [ctaAction?.status, ctaAction?.type, streamingStoryBibleGeneratedEntity]);

  const isFloatingPlan = activePlan && activePlan.status !== "completed" && activePlan.status !== "cancelled";
  const isFloatingCommenting =
    activeCommenting != null && activeCommenting.status !== "completed" && activeCommenting.status !== "cancelled";
  const isFloatingStoryBibleCta =
    ctaAction != null
    && ctaAction.status === "in_progress"
    && isStoryBibleCtaType(ctaAction.type)
    && !streamingStoryBibleGeneratedEntity;

  const currentUserQuestionSignature = useMemo(
    () => getQuestionSignature(currentUserQuestion?.questions),
    [currentUserQuestion],
  );
  const isCurrentQuestionFeedbackTypesPicker =
    !!currentUserQuestion?.questions?.[0]?.question
    && questionIsFeedbackTypesPicker(currentUserQuestion.questions[0].question);
  const shouldSuppressDuplicateQuestionOverlay =
    !!currentUserQuestion
    && !!currentUserQuestionSignature
    && suppressRestoreQuestionSignatureRef.current === currentUserQuestionSignature;
  const shouldSuppressFeedbackTypesReopen =
    !!currentUserQuestion
    && suppressFeedbackTypesPickerRef.current
    && isCurrentQuestionFeedbackTypesPicker;
  const pendingQuestionMessageToRestore = useMemo(
    () => resolvePendingQuestionMessageToRestore(_messages ?? []),
    [_messages],
  );
  const latestAssistantQuestionMessage = useMemo(
    () => [...(_messages ?? [])].reverse().find(
      (message) => message.role === "assistant" && message.metadata?.questionAsked && message.metadata?.userQuestion,
    ),
    [_messages],
  );
  const questionStillOpenOnThread = useMemo(() => {
    const messages = _messages ?? [];
    if (completedResponseAwaitingPersistence && !errorText && ctaAction?.status !== "cancelled") return true;
    if (pendingQuestionMessageToRestore) return true;
    if (
      shouldKeepLiveQuestionWithUserMessageTail(messages, {
        currentTurnUserMessageId,
        ctaCancelled: ctaAction?.status === "cancelled",
        hasStreamError: Boolean(errorText),
      })
    ) {
      return true;
    }
    const lastVisibleAssistant = [...messages].reverse().find(
      (message) => message.role === "assistant" && !message.metadata?.hiddenFromUI,
    );
    return Boolean(
      lastVisibleAssistant?.metadata?.questionAsked
      && !lastVisibleAssistant.metadata?.userQuestion,
    );
  }, [_messages, pendingQuestionMessageToRestore, ctaAction?.status, errorText, currentTurnUserMessageId, completedResponseAwaitingPersistence]);
  const questionMessageIdForAnsweredCheck = resolveQuestionMessageIdForAnsweredCheck({
    liveQuestionSignature: currentUserQuestionSignature,
    pendingMessage: pendingQuestionMessageToRestore,
    latestAssistantQuestionMessage,
  });
  const latestAssistantQuestionAlreadyAnswered = Boolean(
    questionMessageIdForAnsweredCheck
    && (
      answeredQuestionMessageIdsRef.current.has(questionMessageIdForAnsweredCheck)
      || isAssistantQuestionAnsweredInMessages(_messages ?? [], questionMessageIdForAnsweredCheck)
      || isDismissedUserQuestion(projectId, chatId ?? undefined, questionMessageIdForAnsweredCheck)
    ),
  );
  const currentQuestionDismissedBySignature =
    !!currentUserQuestionSignature
    && isDismissedUserQuestionSignature(projectId, chatId ?? undefined, currentUserQuestionSignature);
  const showUserQuestionOverlay = Boolean(
    currentUserQuestion
    && questionStillOpenOnThread
    && !streaming
    && !questionTransitionLocked
    && !shouldSuppressDuplicateQuestionOverlay
    && !shouldSuppressFeedbackTypesReopen
    && !latestAssistantQuestionAlreadyAnswered
    && !currentQuestionDismissedBySignature
  );
  const {
    handleImportFeedbackSkip,
    handleImportFeedbackSubmit,
    showImportFeedbackOverlay,
    visibleImportFeedback,
  } = useImportFeedbackOverlay({
    hidden: showUserQuestionOverlay,
    project,
    projectId,
  });
  const showBlockingBottomOverlay = showUserQuestionOverlay || showImportFeedbackOverlay;
  const showFloatingPlan = isFloatingPlan && !showBlockingBottomOverlay;

  /** Extra scroll padding so the last messages stay above the floating question panel (approx. panel body). */
  const USER_QUESTION_SCROLL_RESERVE = 250;
  const rememberedResponseLanguageCodes = useMemo(
    () =>
      new Set(
        memories
          .map((memory) => parseResponseLanguagePreferenceMemory(memory.content))
          .filter((languageCode): languageCode is string => typeof languageCode === "string" && languageCode.length > 0),
      ),
    [memories],
  );
  const latestAssistantMessage = useMemo(
    () => [...(_messages ?? [])].reverse().find((message) => message.role === "assistant"),
    [_messages],
  );
  const latestAssistantResponseLanguage = useMemo(() => {
    if (!latestAssistantMessage || typeof latestAssistantMessage.metadata?.assistantResponseLanguage !== "string") return null;
    const normalized = normalizeLanguageCode(latestAssistantMessage.metadata.assistantResponseLanguage);
    if (!normalized || normalized.startsWith("en")) return null;
    return normalized;
  }, [latestAssistantMessage]);
  const latestAssistantSavedResponseLanguage = useMemo(() => {
    if (!latestAssistantMessage || !Array.isArray(latestAssistantMessage.metadata?.toolExecutions)) return null;
    for (let i = latestAssistantMessage.metadata.toolExecutions.length - 1; i >= 0; i--) {
      const toolExecution: any = latestAssistantMessage.metadata.toolExecutions[i];
      if (toolExecution?.name !== "manage_preferences") continue;
      const candidateValues: string[] = [];
      if (typeof toolExecution.summary === "string") candidateValues.push(toolExecution.summary);
      const args = toolExecution.arguments;
      if (typeof args === "string") {
        candidateValues.push(args);
        try {
          const parsedArgs = JSON.parse(args);
          if (typeof parsedArgs?.preference === "string") {
            candidateValues.push(parsedArgs.preference);
          }
        } catch {
          // Ignore non-JSON argument payloads.
        }
      } else if (args && typeof args === "object" && typeof (args as { preference?: unknown }).preference === "string") {
        candidateValues.push((args as { preference: string }).preference);
      }
      for (const candidate of candidateValues) {
        const parsedMemory = parseResponseLanguagePreferenceMemory(candidate);
        if (parsedMemory) return parsedMemory;
      }
    }
    return null;
  }, [latestAssistantMessage]);
  const showLanguagePreferenceToast =
    !showUserQuestionOverlay
    && languagePreferenceToast.type !== "hidden";

  useEffect(() => {
    if (isFloatingPlan && messagesContainerRef.current) {
      setTimeout(() => scrollToBottom(), 50);
    }
  }, [isFloatingPlan]);

  useEffect(() => {
    if (isFloatingCommenting && messagesContainerRef.current) {
      setTimeout(() => scrollToBottom(), 50);
    }
  }, [isFloatingCommenting]);

  useEffect(() => {
    if (isFloatingStoryBibleCta && messagesContainerRef.current) {
      setTimeout(() => scrollToBottom(), 50);
    }
  }, [isFloatingStoryBibleCta]);

  useEffect(() => {
    if (showUserQuestionOverlay) return;
    if (!latestAssistantResponseLanguage) {
      setLanguagePreferenceToast((prev) => (prev.type === "hidden" ? prev : { type: "hidden" }));
      return;
    }
    if (rememberedResponseLanguageCodes.has(latestAssistantResponseLanguage)) return;
    if (dismissedLanguagePreferenceCodesRef.current.has(latestAssistantResponseLanguage)) return;
    setLanguagePreferenceToast((current) => {
      if (current.type === "saved") {
        if (latestAssistantResponseLanguage !== current.languageCode) {
          return { type: "prompt", languageCode: latestAssistantResponseLanguage };
        }
        return current;
      }
      if (current.type === "prompt" && current.languageCode === latestAssistantResponseLanguage) return current;
      return { type: "prompt", languageCode: latestAssistantResponseLanguage };
    });
  }, [chatId, latestAssistantResponseLanguage, rememberedResponseLanguageCodes, showUserQuestionOverlay]);

  useEffect(() => {
    if (!latestAssistantSavedResponseLanguage) return;
    dismissedLanguagePreferenceCodesRef.current.add(latestAssistantSavedResponseLanguage);
    setLanguagePreferenceToast((current) => {
      if (current.type !== "hidden") {
        dismissedLanguagePreferenceCodesRef.current.add(current.languageCode);
      }
      return current.type === "hidden" ? current : { type: "hidden" };
    });
  }, [latestAssistantSavedResponseLanguage]);

  const promptLanguageCode =
    languagePreferenceToast.type === "prompt" ? languagePreferenceToast.languageCode : null;
  useEffect(() => {
    if (!promptLanguageCode) return;
    AppEventPublisher.agenticChat.languagePreferenceToastShown({
      language_code: promptLanguageCode,
      language_name: getLanguageDisplayName(promptLanguageCode),
    });
  }, [chatId, promptLanguageCode]);

  useEffect(() => {
    if (languagePreferenceToast.type !== "prompt") return;
    if (!rememberedResponseLanguageCodes.has(languagePreferenceToast.languageCode)) return;
    dismissedLanguagePreferenceCodesRef.current.add(languagePreferenceToast.languageCode);
    setLanguagePreferenceToast({ type: "hidden" });
  }, [languagePreferenceToast, rememberedResponseLanguageCodes]);

  const dismissLanguagePreferenceToast = useCallback((dismissedLanguageCode?: string, surface?: "no_button" | "toast_close") => {
    if (dismissedLanguageCode && surface) {
      AppEventPublisher.agenticChat.languagePreferenceToastDismissed({
        language_code: dismissedLanguageCode,
        surface,
      });
    }
    if (dismissedLanguageCode) {
      dismissedLanguagePreferenceCodesRef.current.add(dismissedLanguageCode);
    }
    setLanguagePreferenceToast({ type: "hidden" });
  }, []);

  const handleLanguagePreferenceSave = useCallback(async () => {
    if (languagePreferenceToast.type !== "prompt") return;
    const languageCode = languagePreferenceToast.languageCode;
    const nextContent = buildResponseLanguagePreferenceMemory(languageCode);
    const existing = memories.find((memory) => parseResponseLanguagePreferenceMemory(memory.content) !== null);
    AppEventPublisher.agenticChat.languagePreferenceSaveClicked({
      language_code: languageCode,
      language_name: getLanguageDisplayName(languageCode),
      had_existing_preference: !!existing,
    });
    setLanguagePreferenceSaving(true);
    try {
      if (existing) {
        if (existing.content !== nextContent) {
          await updateMemory.mutateAsync({ id: existing.id, content: nextContent });
        }
      } else {
        await addMemory.mutateAsync(nextContent);
      }
      dismissedLanguagePreferenceCodesRef.current.add(languageCode);
      setLanguagePreferenceToast({ type: "saved", languageCode });
      AppEventPublisher.agenticChat.languagePreferenceSaveSucceeded({ language_code: languageCode });
    } catch {
      AppEventPublisher.agenticChat.languagePreferenceSaveFailed({ language_code: languageCode });
      setToast("Could not save language preference. Please try again.");
    } finally {
      setLanguagePreferenceSaving(false);
    }
  }, [addMemory, languagePreferenceToast, memories, setToast, updateMemory]);

  const openChatPreferencesFromToast = useCallback(() => {
    AppEventPublisher.agenticChat.languagePreferenceOpenPreferencesClicked();
    openChatPreferences({ advanced: true, source: "language_preference_toast" });
  }, []);

  useEffect(() => {
    const current = usePersistentChatStore.getState().activeCommenting;
    const isFeedbackRunCommenting = !!current?.id?.startsWith("feedback-run-");
    if (!isFeedbackRunCommenting) {
      feedbackRunSeenActiveRef.current = false;
      return;
    }

    if (isFeedbackRunning) {
      feedbackRunSeenActiveRef.current = true;
      return;
    }

    if (current?.status === "in_progress" && feedbackRunSeenActiveRef.current) {
      usePersistentChatStore.setState({
        activeCommenting: { ...current, status: "completed" },
      });
      const timeout = setTimeout(() => {
        const latest = usePersistentChatStore.getState().activeCommenting;
        if (latest?.id === current.id && latest.status === "completed") {
          usePersistentChatStore.setState({ activeCommenting: null });
        }
      }, 1200);
      return () => clearTimeout(timeout);
    }
  }, [isFeedbackRunning, activeCommenting?.id, activeCommenting?.status]);

  // Reset submittedAnswer when a new question arrives (not when cleared)
  useEffect(() => {
    if (currentUserQuestion) {
      const incomingSignature = getQuestionSignature(currentUserQuestion.questions);
      const suppressSignature = suppressRestoreQuestionSignatureRef.current;
      const isSameSuppressedQuestion = !!suppressSignature && suppressSignature === incomingSignature
        && (!pendingQuestionMessageToRestore?.id
          || pendingQuestionMessageToRestore.id === suppressRestoreQuestionMessageIdRef.current);
      if (!isSameSuppressedQuestion) {
        setSubmittedAnswer(null);
        suppressRestoreQuestionMessageIdRef.current = null;
        suppressRestoreQuestionSignatureRef.current = null;
        if (questionTransitionLockTimeoutRef.current !== null) {
          clearTimeout(questionTransitionLockTimeoutRef.current);
          questionTransitionLockTimeoutRef.current = null;
        }
        setQuestionTransitionLocked(false);
      }
      // Once a different question arrives, allow feedback-types picker again for future cycles.
      if (!isCurrentQuestionFeedbackTypesPicker) {
        suppressFeedbackTypesPickerRef.current = false;
      }
      // Track which chat this question belongs to
      if (chatId) {
        questionChatIdRef.current = chatId;
      }
    }
  }, [currentUserQuestion, chatId, isCurrentQuestionFeedbackTypesPicker, pendingQuestionMessageToRestore]);

  useEffect(() => {
    setSubmittedAnswer(null);
  }, [chatId]);

  useEffect(() => {
    return () => {
      if (questionTransitionLockTimeoutRef.current !== null) {
        clearTimeout(questionTransitionLockTimeoutRef.current);
      }
    };
  }, []);

  /** Visible persisted assistant for the active turn, falling back to the visible tail before the server maps the turn. */
  const persistedStreamingMessage = useMemo(() => {
    const visible = (_messages || []).filter((m) => !m.metadata?.hiddenFromUI
      && (!currentRetryRequestId || m.role !== "assistant"
        || m.metadata?.clientRequestId === currentRetryRequestId
        || m.id === currentRetryAssistantMessageId));
    if (currentTurnUserMessageId) {
      const matchingAssistant = [...visible]
        .reverse()
        .find((message) => message.role === "assistant" && message.metadata?.triggerUserMessageId === currentTurnUserMessageId);
      if (matchingAssistant) {
        return matchingAssistant;
      }
      return undefined;
    }
    // Hidden question answers still start a new turn. Never attach its live
    // state to the previous question while waiting for chat_acknowledged.
    const tail = _messages[_messages.length - 1];
    if (tail?.metadata?.hiddenFromUI) return undefined;
    if (tail?.metadata?.questionAsked && (
      submittedAnswer?.questionMessageId === tail.id
      || tail.metadata.questionAnswerPreview
    )) return undefined;
    return tail;
  }, [_messages, currentTurnUserMessageId, submittedAnswer, currentRetryRequestId, currentRetryAssistantMessageId]);

  const messages: PersistentChatMessageData[] = useMemo(() => {
    const visibleMessages = mergeFrozenChatReplies(_messages.filter((m) => !m.metadata?.hiddenFromUI), frozenReplies, projectId, chatId ?? NEW_CHAT_ID);
    // Show error message if present
    if (!streaming && errorText) {
      return [
        ...visibleMessages,
        {
          id: "error",
          role: "assistant",
          content: errorText,
          created_at: new Date(),
          metadata: {},
          user_id: "",
          chat_id: "",
        },
      ];
    }

    // History can publish the final execution row before its body. Keep the
    // local recap in that row until the saved summary can replace it atomically.
    const summaryRow = persistedStreamingMessage;
    if (completedResponseAwaitingPersistence && completedPlanSummary
      && summaryRow?.role === "assistant" && !summaryRow.content?.trim()
      && summaryRow.metadata?.planExecution === true
      && summaryRow.metadata?.plan?.id === completedPlanSummaryId
      && summaryRow.metadata?.plan?.status === "completed") {
      return visibleMessages.map(message => message.id === summaryRow.id
        ? { ...message, content: currentStreamingText || completedPlanSummary }
        : message);
    }

    // Once the current reply has a saved row, keep its body current there. After
    // streaming ends, retain local text only when the saved body is still empty.
    if (currentStreamingText?.trim() && persistedStreamingMessage?.role === "assistant") {
      const pm = persistedStreamingMessage;
      const forCurrentTurn =
        !currentTurnUserMessageId || pm.metadata?.triggerUserMessageId === currentTurnUserMessageId;
      if (forCurrentTurn && (streaming || !pm.content?.trim())) {
        const idx =
          pm.id != null && pm.id !== ""
            ? visibleMessages.findIndex((m) => m.id === pm.id)
            : (() => {
                for (let i = visibleMessages.length - 1; i >= 0; i--) {
                  const m = visibleMessages[i];
                  if (
                    m.role === "assistant"
                    && (!currentTurnUserMessageId || m.metadata?.triggerUserMessageId === currentTurnUserMessageId)
                    && !m.content?.trim()
                  ) {
                    return i;
                  }
                }
                return -1;
              })();
        if (idx !== -1) {
          const patched = [...visibleMessages];
          patched[idx] = {
            ...pm,
            content: currentStreamingText,
            metadata: currentTurnPassage && !pm.metadata?.passage
              ? { ...pm.metadata, passage: currentTurnPassage }
              : pm.metadata,
          };
          return patched;
        }
      }
    }

    return visibleMessages;
  }, [_messages, errorText, streaming, currentStreamingText, persistedStreamingMessage, currentTurnUserMessageId, currentTurnPassage, frozenReplies, projectId, chatId, completedResponseAwaitingPersistence, completedPlanSummary, completedPlanSummaryId]);

  const terminalPlanCardMessageIds = useMemo(
    () => selectTerminalPlanCardMessageIds(messages),
    [messages],
  );

  const terminalCommentingCardMessageIds = useMemo(
    () => selectTerminalCommentingCardMessageIds(messages),
    [messages],
  );

  const stalledConversationState = useMemo(() => {
    if (agentIsRunning || queuedSend || completedResponseAwaitingPersistence || chatId === NEW_CHAT_ID) return null;
    if (currentUserQuestion) return null;
    if (ctaAction?.status === "awaiting_input" || ctaAction?.status === "cancelled") return null;
    // The display-only error bubble is not a completed assistant turn. Keep
    // frozen partial replies in the path, but exclude that synthetic row.
    const last = interruptedChatRetryUserMessage(messages.filter(message => message.id !== "error"));
    if (!last?.id) return null;
    if (isCtaSeededUserMessage(last)) return null;
    const content = (last.content ?? "").trim();
    if (!content) return null;
    const modeFromMessage = (last.metadata?.mode as "write" | "chat" | undefined) ?? (userSettings.agenticChatMode as "write" | "chat");
    return { messageId: last.id, content, mode: modeFromMessage };
  }, [streaming, pendingAgentResponse, chatId, messages, queuedSend, userSettings.agenticChatMode, currentUserQuestion, ctaAction?.type, ctaAction?.status, agentIsRunning, completedResponseAwaitingPersistence]);

  const tailAwaitingReplyUserMessageId = useMemo(() => {
    if (chatId === NEW_CHAT_ID) return null;
    if (activePlan && (activePlan.status === "in_progress" || activePlan.status === "paused")) {
      return null;
    }

    const inFlight = agentIsRunning;
    if (inFlight) {
      if (currentTurnUserMessageId) return currentTurnUserMessageId;
      const visible = (_messages || []).filter((m) => !m.metadata?.hiddenFromUI);
      const last = visible[visible.length - 1];
      if (last?.role === "user" && (last.content ?? "").trim()) return last.id ?? null;
      return null;
    }

    return stalledConversationState?.messageId ?? null;
  }, [
    chatId,
    activePlan,
    agentIsRunning,
    currentTurnUserMessageId,
    _messages,
    stalledConversationState?.messageId,
  ]);

  const optimisticUserMessageId = usePersistentChatStore((s) => s.optimisticUserMessageId);
  const userMessageIdsWithVisibleAssistantReplySet = useMemo(() => {
    const ids = userMessageIdsWithVisibleAssistantReply(branchRawMessages as any);
    if (tailAwaitingReplyUserMessageId) {
      ids.add(tailAwaitingReplyUserMessageId);
    }
    if (optimisticUserMessageId && agentIsRunning) {
      ids.add(optimisticUserMessageId);
    }
    return ids;
  }, [
    branchRawMessages,
    tailAwaitingReplyUserMessageId,
    optimisticUserMessageId,
    agentIsRunning,
  ]);

  const canShowInlineEditForUserMessageId = useCallback(
    (messageId: string) => {
      if (chatId === NEW_CHAT_ID) return false;
      if (activePlan && (activePlan.status === "in_progress" || activePlan.status === "paused")) {
        return false;
      }
      const msg = _messages.find((m) => m.id === messageId);
      if (!msg || msg.role !== "user" || !(msg.content ?? "").trim()) return false;
      if ((msg.metadata as { planApproval?: boolean })?.planApproval) return false;
      if (msg.metadata?.hiddenFromUI) return false;
      if (agentIsRunning) {
        return messageId === tailAwaitingReplyUserMessageId;
      }
      return true;
    },
    [
      chatId,
      activePlan,
      _messages,
      agentIsRunning,
      tailAwaitingReplyUserMessageId,
    ],
  );

  const showCommentingCardInline = !!stalledConversationState && isFloatingCommenting && !currentStreamingText;
  const isFloatingCommentingVisible = isFloatingCommenting && !showCommentingCardInline;
  const floatingCardsOffsetAboveInput =
    (showFloatingPlan && planCardHeight > 0 ? planCardHeight + 8 : showFloatingPlan || isFloatingCommentingVisible || isFloatingStoryBibleCta ? 4 : 0)
    + (isFloatingCommentingVisible ? commentingCardHeight : 0)
    + (isFloatingStoryBibleCta ? storyBibleCtaCardHeight : 0);
  const {
    mountSurvey: mountChatExperienceSurvey,
    showSurvey: showChatExperienceSurvey,
    handleSubmit: handleChatExperienceSurveySubmit,
    handleSkip: handleChatExperienceSurveySkip,
  } = useChatExperienceSurvey({
    chatId,
    projectId,
    messages: _messages ?? [],
    messagesLoaded: !isLoading,
    streaming,
    pendingAgentResponse: Boolean(pendingAgentResponse),
    hidden: showBlockingBottomOverlay || isPlanEditing,
    mode: agenticChatMode,
    modelKey: userSettings.getModelKey(),
    newChatMaterializationId: pendingNewChatMaterializationId,
  });
  const chatExperienceSurveyScrollReserve = getFloatingSurveyScrollReserve(
    showChatExperienceSurvey,
    chatExperienceSurveyHeight,
  );

  useEffect(() => {
    if (!showChatExperienceSurvey) {
      setChatExperienceSurveyHeight(0);
      return;
    }
    const el = chatExperienceSurveyRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      if (!entries[0]) return;
      const height = entries[0].borderBoxSize?.[0]?.blockSize ?? entries[0].contentRect.height;
      setChatExperienceSurveyHeight(height);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [showChatExperienceSurvey]);
  const remainingCredits = useMemo(() => {
    const candidates = [
      subscriptionJSON?.totalQuota,
      subscriptionJSON?.quota,
      subscriptionJSON?.data?.quota,
    ];
    for (const value of candidates) {
      if (typeof value === "number" && Number.isFinite(value)) {
        return value;
      }
    }
    return null;
  }, [subscriptionJSON]);
  const isOutOfCredits = remainingCredits != null && remainingCredits <= 0;
  const isDiscussModeBlockedForTrial = !discussModeAvailable && Boolean(subscriptionJSON?.activeTrial);
  const showOutOfCreditsToast = useCallback(() => {
    if (subscriptionJSON?.activeTrial) {
      setToast("You're out of trial credits. Start a membership to keep chatting.");
      return;
    }
    setToast("You're out of credits. Add more credits to keep chatting.");
  }, [setToast, subscriptionJSON]);
  const isSendBlockedForTrial = useCallback(
    (mode: "write" | "chat") => {
      if (subscriptionJSON?.activeTrial) {
        return isTrialAgenticChatSendBlocked(trialFreeChatSubscriptionView, mode);
      }
      return mode === "write" && isOutOfCredits;
    },
    [isOutOfCredits, subscriptionJSON?.activeTrial, trialFreeChatSubscriptionView],
  );
  const showTrialSendBlockedToast = useCallback(
    (mode: "write" | "chat") => {
      if (subscriptionJSON?.activeTrial && mode === "chat" && isDiscussModeBlockedForTrial) {
        setToast(
          `Your complimentary ${composerModeLabels.chatLabel} week has ended. Switch to ${composerModeLabels.writeLabel} to use your trial credits.`,
        );
        return;
      }
      showOutOfCreditsToast();
    },
    [
      composerModeLabels.chatLabel,
      composerModeLabels.writeLabel,
      isDiscussModeBlockedForTrial,
      setToast,
      showOutOfCreditsToast,
      subscriptionJSON?.activeTrial,
    ],
  );

  const handleResendLastMessage = useCallback(() => {
    if (!stalledConversationState?.content?.trim() || streaming) return;
    const mode = stalledConversationState.mode === "write" ? "write" : "chat";
    if (isSendBlockedForTrial(mode)) {
      showTrialSendBlockedToast(mode);
      return;
    }
    usePersistentChatStore.setState({ errorText: "" });
    AppEventPublisher.agenticChat.messageResent({
      mode: stalledConversationState.mode === "write" ? AgenticChatMode.Write : AgenticChatMode.Chat,
      model: userSettings.getModelKey(),
    });
    void sendMessage(
      stalledConversationState.content,
      userSettings.getModelKey(),
      stalledConversationState.mode,
      undefined,
      undefined,
      { resendOfMessageId: stalledConversationState.messageId, resendIntent: "retry" },
    );
    setTimeout(() => scrollToBottom(), 50);
  }, [stalledConversationState, streaming, isSendBlockedForTrial, showTrialSendBlockedToast, sendMessage, userSettings, scrollToBottom]);

  const handleDuplicatePlanRetry = useCallback(
    async (plan: PlanData) => {
      if (streaming) return;
      if (activePlan && activePlan.status !== "completed" && activePlan.status !== "cancelled") {
        setToast("Finish or cancel your current plan first.");
        return;
      }
      const steps = plan.steps ?? [];
      AppEventPublisher.agenticChat.planTryAgainClicked({
        planId: plan.id,
        planStatus: plan.status === "cancelled" ? "cancelled" : "completed",
        stepCount: steps.length,
        completedStepCount: steps.filter((s) => s.status === "completed").length,
      });
      try {
        const res = await Auth.fetchWithIdToken("/api/ai/agenticChat/plan/duplicate", {
          chatId,
          projectId,
          sourcePlanId: plan.id,
        });
        if (res?.success && res.plan) {
          setPlan(res.plan);
          queryClient.invalidateQueries(["chat", chatId]);
          queryClient.invalidateQueries(["chats", projectId]);
          await queryClient.refetchQueries(["chat", chatId]);
        }
      } catch (e) {
        Sentry.captureException(e);
        setToast("Could not duplicate plan. Try again.");
      }
      setTimeout(() => scrollToBottom(), 50);
    },
    [streaming, activePlan, chatId, projectId, queryClient, setPlan, scrollToBottom, setToast],
  );

  const handleRetryCommentCreation = useCallback(
    (triggerUserMessageId: string, content: string, mode: "write" | "chat") => {
      if (streaming || mode !== "write") return;
      const userMessage = _messages.find((m) => m.id === triggerUserMessageId);
      const messageContent = userMessage?.content ?? content;
      if (!messageContent) return;
      void sendMessage(messageContent, userSettings.getModelKey(), mode);
      setTimeout(() => scrollToBottom(), 50);
    },
    [streaming, _messages, sendMessage, userSettings, scrollToBottom],
  );

  const handleFeedbackTryAgain = useCallback(
    (params: {
      assistantMessageId: string;
      precedingUserMessageId: string | null;
      mode: "write" | "chat";
      feedbackText?: string;
    }) => {
      if (agentIsRunning) return;
      if (isSendBlockedForTrial(params.mode)) {
        showTrialSendBlockedToast(params.mode);
        return;
      }

      const retryMessage = params.feedbackText?.trim()
        ? `${DIFFERENT_APPROACH_RETRY_MESSAGE}\n\nAdditional context: ${params.feedbackText.trim()}`
        : DIFFERENT_APPROACH_RETRY_MESSAGE;

      void sendMessage(retryMessage, userSettings.getModelKey(), params.mode, false, undefined, {
        userMessageMetadata: {
          feedbackRetry: true,
          dislikedAssistantMessageId: params.assistantMessageId,
          ...(params.feedbackText?.trim() ? { feedbackText: params.feedbackText.trim() } : {}),
        },
      });
      setTimeout(() => scrollToBottom(), 50);
    },
    [agentIsRunning, isSendBlockedForTrial, showTrialSendBlockedToast, sendMessage, userSettings, scrollToBottom],
  );

  const handleFeedbackSwitchToChatMode = useCallback(() => {
    if (!discussModeAvailable) {
      setToast(
        `Your complimentary ${composerModeLabels.chatLabel} week has ended. Keep using ${composerModeLabels.writeLabel} with your trial credits.`,
      );
      return;
    }
    updateUserSettings({ agenticChatMode: "chat" });
    setToast("Switched to Chat mode for guidance-only help.");
  }, [composerModeLabels.chatLabel, composerModeLabels.writeLabel, discussModeAvailable, setToast, updateUserSettings]);

  const handleFeedbackStartFreshChat = useCallback(() => {
    void dismissImportConversationStarters(useProjectStore.getState().project);
    setChatId(NEW_CHAT_ID);
    setToast("Started a new chat.");
  }, [setChatId, setToast]);

  const handleStartEditUserMessage = useCallback(
    (userMessageId: string) => {
      if (activePlan && (activePlan.status === "in_progress" || activePlan.status === "paused")) return;
      const isActiveTurnTail =
        agentIsRunning && tailAwaitingReplyUserMessageId === userMessageId;
      if (isActiveTurnTail) {
        void usePersistentChatStore.getState().abortStream("replacement");
        const sb = useStoryBibleStore.getState();
        sb.abortFeedback();
        if (sb.feedbackPending && sb.feedbackTriggerSource === "agentic_chat") {
          sb.clearFeedbackPending();
        }
        cancelCommenting();
      }
      setInlineEditUserMessageId(userMessageId);
    },
    [activePlan, agentIsRunning, cancelCommenting, tailAwaitingReplyUserMessageId],
  );

  const handleSubmitInlineEditUserMessage = useCallback(
    async (userMessageId: string, text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      if (activePlan && (activePlan.status === "in_progress" || activePlan.status === "paused")) return;

      const msg = _messages.find((m) => m.id === userMessageId);
      if (!msg || msg.role !== "user") return;
      const mode =
        (msg.metadata?.mode as "write" | "chat" | undefined) ?? (userSettings.agenticChatMode as "write" | "chat");
      if (isSendBlockedForTrial(mode)) {
        showTrialSendBlockedToast(mode);
        return;
      }

      setInlineEditUserMessageId(null);
      usePersistentChatStore.setState({ errorText: "" });
      clearDismissedUserQuestionSignatures(projectId, chatId ?? undefined);
      suppressRestoreQuestionMessageIdRef.current = null;
      suppressRestoreQuestionSignatureRef.current = null;
      clearCurrentUserQuestion();

      AppEventPublisher.agenticChat.messageResent({
        mode: mode === "write" ? AgenticChatMode.Write : AgenticChatMode.Chat,
        model: userSettings.getModelKey(),
      });
      void sendMessage(trimmed, userSettings.getModelKey(), mode, undefined, undefined, {
        resendOfMessageId: userMessageId,
        resendIntent: "edit",
      });
      setTimeout(() => scrollToBottom(), 50);
    },
    [_messages, activePlan, isSendBlockedForTrial, showTrialSendBlockedToast, userSettings, sendMessage, scrollToBottom],
  );

  useEffect(() => {
    if (!inlineEditUserMessageId) return;
    const frame = requestAnimationFrame(() => {
      document
        .querySelector(`[data-inline-edit-user-message="${inlineEditUserMessageId}"]`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    return () => cancelAnimationFrame(frame);
  }, [inlineEditUserMessageId]);

  const lastAssistantMessageId = useMemo(
    () => getLastAssistantMessageForPlanSync(messages)?.id,
    [messages],
  );

  const messageCountRef = useRef(_messages?.length ?? 0);
  messageCountRef.current = _messages?.length ?? 0;
  const sendMessageRef = useRef(sendMessage);
  sendMessageRef.current = sendMessage;
  const activeHighlightRef = useRef(activeHighlight);
  activeHighlightRef.current = activeHighlight;
  const chatIdForComposerRef = useRef(chatId);
  chatIdForComposerRef.current = chatId;
  const agenticChatModeRef = useRef(agenticChatMode);
  agenticChatModeRef.current = agenticChatMode;

  const onAgenticChatModeChange = useCallback((mode: string) => {
    if (mode === "chat" && !discussModeAvailable) {
      return;
    }
    updateUserSettingsRef.current({ agenticChatMode: mode });
  }, [discussModeAvailable]);
  const onAgenticChatBrainModelChange = useCallback((modelKey: string) => {
    updateUserSettingsRef.current({ agenticChatBrainModel: modelKey });
  }, []);

  const handleComposerAttachmentsChange = useCallback((docs: AttachmentDoc[]) => {
    setComposerAttachmentDocs(docs);
  }, []);

  const handleRemoveComposerAttachment = useCallback((doc: AttachmentDoc) => {
    composerRef.current?.removeAttachment(doc.id);
  }, []);

  const handleComposerSend = useCallback(
    (base: string, meta: { hasAttachments: boolean; hasMentions: boolean; pastedExcerpts: string[] }) => {
      const mode = agenticChatModeRef.current as "write" | "chat";
      if (isSendBlockedForTrial(mode)) {
        showTrialSendBlockedToast(mode);
        return;
      }

      AppEventPublisher.agenticChat.messageSent({
        mode: agenticChatModeRef.current === "write" ? AgenticChatMode.Write : AgenticChatMode.Chat,
        model: getModelKeyRef.current(),
        chatLength: messageCountRef.current + 1,
        hasAttachments: meta.hasAttachments,
        hasMentions: meta.hasMentions,
        hasPassage: !!activeHighlightRef.current?.trim(),
        isNewChat: chatIdForComposerRef.current === NEW_CHAT_ID,
      });

      sendMessageRef.current(
        base,
        getModelKeyRef.current(),
        agenticChatModeRef.current as "write" | "chat",
        false,
        undefined,
        { pastedExcerpts: meta.pastedExcerpts },
      );

      sentThisSessionRef.current = true;
      setTimeout(() => scrollToNewMessage(), 50);
    },
    [isSendBlockedForTrial, showTrialSendBlockedToast, scrollToNewMessage],
  );

  /** Stop agentic SSE and any manuscript Feedback started from this turn (tool run). */
  const abortAgentStreamAndLinkedManuscriptFeedback = useCallback((source: "stop" | "cta_cancel" | "skip_question" | "feedback_cancel") => {
    void usePersistentChatStore.getState().abortStream(source);
    const sb = useStoryBibleStore.getState();
    sb.abortFeedback();
    if (sb.feedbackPending && sb.feedbackTriggerSource === "agentic_chat") {
      sb.clearFeedbackPending();
    }
  }, []);

  const handleStopAgent = useCallback(() => {
    abortAgentStreamAndLinkedManuscriptFeedback("stop");
    cancelCommenting();
  }, [abortAgentStreamAndLinkedManuscriptFeedback, cancelCommenting]);

  const handleStarterClick = useCallback(
    (message: string, _requiresWriteMode: boolean) => {
      // Never auto-switch mode from conversation starters: send in the user's current mode so
      // the agent can brainstorm in Chat mode and only switch when the user chooses Write.
      sendMessageRef.current(
        message,
        getModelKeyRef.current(),
        agenticChatModeRef.current as "write" | "chat",
      );
      sentThisSessionRef.current = true;
      setTimeout(() => scrollToNewMessage(), 50);
    },
    [scrollToNewMessage],
  );

  const sendUserQuestionResponse = useCallback(
    (pairs: UserQuestionAnswerPair[]) => {
      const baseBlock = formatUserQuestionAnswerBlock(pairs);
      const questionState = usePersistentChatStore.getState();
      const savedQuestion = questionState.currentUserQuestion;
      // A live prompt may belong to a turn newer than every cached question.
      // Until it reconciles, let the server select the parent and avoid replaying
      // an older question's answer preview, CTA state, or manuscript target.
      const questionHistory = questionState.completedResponseAwaitingPersistence ? [] : (_messages ?? []);
      const questionMessageId = [...questionHistory]
        .reverse()
        .find((m) => m.role === "assistant" && m.metadata?.questionAsked)?.id;
      const answer =
        savedQuestion?.questions?.length ?
          appendDocumentMentionsForAmbiguousManuscriptQuestions(baseBlock, savedQuestion.questions, pairs)
        : { text: baseBlock, hasAppendedDocumentMentions: false };
      const isFeedbackPickerSubmission =
        modeLabelVariant === "feedback_beta"
        && pairs.length === 1
        && questionIsFeedbackTypesPicker(pairs[0].question)
        && pairs[0].answer.trim().length > 0
        && pairs[0].answer.trim() !== USER_QUESTION_NO_PREFERENCE;
      const fallbackQuestionMessageId = [...questionHistory]
        .reverse()
        .find((m) => m.role === "assistant" && (m.metadata?.questionAsked || m.metadata?.userQuestion))
        ?.id;
      const resolvedQuestionMessageId = questionMessageId ?? fallbackQuestionMessageId;
      if (resolvedQuestionMessageId) {
        answeredQuestionMessageIdsRef.current.add(resolvedQuestionMessageId);
      }
      suppressRestoreQuestionMessageIdRef.current = resolvedQuestionMessageId ?? null;
      suppressRestoreQuestionSignatureRef.current = getQuestionSignature(savedQuestion?.questions);
      const answeredAssistant =
        findAssistantMessageForUserQuestion(questionHistory, savedQuestion ?? undefined)
        ?? (resolvedQuestionMessageId
          ? questionHistory.find((message) => message.id === resolvedQuestionMessageId)
          : undefined);
      const pendingDraftFromAnsweredAssistant = findPendingDraftReplayForQuestionAnswer(
        questionHistory,
        answeredAssistant,
      );
      const replayPassage = pendingDraftFromAnsweredAssistant?.passage?.trim() ?? "";
      const replayEditorSelectionHint = pendingDraftFromAnsweredAssistant?.editorSelectionHint;
      const consentReplay = resolveConsentReplayFromActiveHighlight(
        replayPassage,
        replayEditorSelectionHint,
      );
      const effectiveReplayPassage = consentReplay.passage;
      const effectiveReplayEditorSelectionHint = consentReplay.hint;
      if (shouldStartGenerateCastProgressOnQuestionAnswer(answeredAssistant, savedQuestion, pairs)) {
        const existingCta = usePersistentChatStore.getState().ctaAction;
        setCtaAction({
          type: "generate_cast",
          status: "in_progress",
          displayTitle: existingCta?.displayTitle ?? "Generate Cast",
          userMessage: existingCta?.userMessage ?? "Generate all characters",
          cannedResponse: existingCta?.cannedResponse ?? "",
          customInstructions: existingCta?.customInstructions,
          signalKey: existingCta?.signalKey,
        });
        const materializedCtaChatId = usePersistentChatStore.getState().pendingNewChatMaterializationId;
        if (materializedCtaChatId) {
          setChatId(materializedCtaChatId);
        }
      }
      if (isFeedbackPickerSubmission) {
        suppressFeedbackTypesPickerRef.current = true;
      }
      if (questionTransitionLockTimeoutRef.current !== null) {
        clearTimeout(questionTransitionLockTimeoutRef.current);
      }
      setQuestionTransitionLocked(true);
      questionTransitionLockTimeoutRef.current = window.setTimeout(() => {
        questionTransitionLockTimeoutRef.current = null;
        setQuestionTransitionLocked(false);
      }, 1200);
      setSubmittedAnswer(questionMessageId ? { ...answer, questionMessageId } : null);
      if (chatId) {
        unansweredQuestionsByChatRef.current.delete(chatId);
      }
      clearCurrentUserQuestion();
      questionChatIdRef.current = null;
      const affirmsAllowEditsSwitch =
        userSettings.agenticChatMode === "chat" && userQuestionAnswerAffirmsAllowEditsSwitch(pairs);
      const sendMode: "write" | "chat" = affirmsAllowEditsSwitch ? "write" : (userSettings.agenticChatMode as "write" | "chat");
      if (affirmsAllowEditsSwitch) {
        markAutoSwitchedFromChatToWrite();
        AppEventPublisher.agenticChat.modeSwitch(AgenticChatMode.Chat, AgenticChatMode.Write);
        updateUserSettings({ agenticChatMode: "write" });
      }
      void sendMessage(
        answer.text,
        userSettings.getModelKey(),
        sendMode,
        true,
        (errMsg) => {
          Sentry.captureException(new Error(errMsg));
          setInlineToast("Could not send your answer. Please try again.");
          suppressRestoreQuestionMessageIdRef.current = null;
          suppressRestoreQuestionSignatureRef.current = null;
          suppressFeedbackTypesPickerRef.current = false;
          if (questionTransitionLockTimeoutRef.current !== null) {
            clearTimeout(questionTransitionLockTimeoutRef.current);
            questionTransitionLockTimeoutRef.current = null;
          }
          setQuestionTransitionLocked(false);
          if (savedQuestion) {
            usePersistentChatStore.setState({ currentUserQuestion: savedQuestion });
            if (chatId) {
              questionChatIdRef.current = chatId;
            }
          }
        },
        {
          userMessageMetadata: {
            questionAnswer: true,
            ...(answer.hasAppendedDocumentMentions === true
              ? { appendedDocumentMentions: true }
              : {}),
            ...(effectiveReplayPassage
              ? {
                  rewriteTargetText: effectiveReplayPassage,
                  passage: effectiveReplayPassage,
                }
              : {}),
            ...(effectiveReplayEditorSelectionHint
              ? { editorSelectionHint: effectiveReplayEditorSelectionHint }
              : {}),
            ...(isFeedbackPickerSubmission ? { feedbackTypesPickerSubmission: true } : {}),
          },
          ...(effectiveReplayPassage ? { passageOverride: effectiveReplayPassage } : {}),
          ...(effectiveReplayEditorSelectionHint
            ? { editorSelectionHintOverride: effectiveReplayEditorSelectionHint }
            : {}),
          ...(resolvedQuestionMessageId ? { parentMessageId: resolvedQuestionMessageId } : {}),
        },
      );
      setTimeout(() => scrollToBottom(), 50);
    },
    [sendMessage, userSettings, updateUserSettings, scrollToBottom, clearCurrentUserQuestion, chatId, _messages, modeLabelVariant, setCtaAction, setChatId],
  );

  const handleSkipUserQuestion = useCallback(
    () => {
      const savedQuestion = usePersistentChatStore.getState().currentUserQuestion;
      const questionMessageId = [...(_messages ?? [])]
        .reverse()
        .find((m) => m.role === "assistant" && m.metadata?.questionAsked)?.id;
      const fallbackQuestionMessageId = [...(_messages ?? [])]
        .reverse()
        .find((m) => m.role === "assistant" && (m.metadata?.questionAsked || m.metadata?.userQuestion))
        ?.id;
      const resolvedQuestionMessageId = questionMessageId ?? fallbackQuestionMessageId;
      if (resolvedQuestionMessageId) {
        answeredQuestionMessageIdsRef.current.add(resolvedQuestionMessageId);
        markDismissedUserQuestion(projectId, chatId ?? undefined, resolvedQuestionMessageId);
      }
      const dismissedQuestionSignature = getQuestionSignature(savedQuestion?.questions);
      if (dismissedQuestionSignature) {
        markDismissedUserQuestionSignature(projectId, chatId ?? undefined, dismissedQuestionSignature);
      }
      suppressRestoreQuestionMessageIdRef.current = resolvedQuestionMessageId ?? null;
      suppressRestoreQuestionSignatureRef.current = dismissedQuestionSignature;
      if (chatId) {
        unansweredQuestionsByChatRef.current.delete(chatId);
      }
      clearCurrentUserQuestion();
      questionChatIdRef.current = null;

      abortAgentStreamAndLinkedManuscriptFeedback("skip_question");
      cancelCommenting();

      const storePlan = usePersistentChatStore.getState().activePlan;
      if (
        storePlan
        && storePlan.status !== "completed"
        && storePlan.status !== "cancelled"
        && chatId
      ) {
        void cancelPlan({ chatId, projectId, planId: storePlan.id, setChatId });
      }
    },
    [
      abortAgentStreamAndLinkedManuscriptFeedback,
      cancelCommenting,
      cancelPlan,
      clearCurrentUserQuestion,
      chatId,
      _messages,
      projectId,
      setChatId,
    ],
  );

  const handleSuggestedActionClick = useCallback((message: string, switchToWrite: boolean) => {
    void switchToWrite;
    sendMessage(message, userSettings.getModelKey(), userSettings.agenticChatMode as "write" | "chat");
    sentThisSessionRef.current = true;
    setTimeout(() => scrollToNewMessage(), 50);
  }, [sendMessage, userSettings, scrollToNewMessage]);

  const handleCtaGo = useCallback((customInstructions: string, extra?: {
    role?: string;
    roleLabel?: string;
    fields?: Array<{ id?: string; label: string; value?: unknown }>;
    roleExplicitlySelected?: boolean;
  }) => {
    // Read from the store so instant-fire CTAs (InstantFireInput) can't run with a stale closure
    // where React still had `ctaAction === null` when the handler was created. Also require
    // `awaiting_input` so `onGo` identity staying stable won't double-send after we transition.
    const action = usePersistentChatStore.getState().ctaAction;
    if (!action || action.status !== "awaiting_input") return;
    const resolvedCustomInstructions = customInstructions || action.customInstructions;

    const userData = useSudowriteStore.getState().userData;
    const persistedComposerMode = getComposerAgenticChatMode(userData);
    const shouldSwitchToWrite = persistedComposerMode === "chat";
    const sendMode = shouldSwitchToWrite ? "write" : persistedComposerMode as "write" | "chat";
    if (shouldSwitchToWrite) {
      promoteComposerToAllowEditsForStoryBibleCta({
        userData,
        onModeChange: () => updateUserSettings({ agenticChatMode: "write" }),
      });
    }

    const ctaActionPayload = {
      type: action.type,
      targetSurface: action.targetSurface,
      targetId: action.targetId,
      targetName: action.targetName,
      customInstructions: resolvedCustomInstructions || undefined,
      outlineFramework: action.outlineFramework,
      characterId: action.characterId,
      entryId: action.entryId,
      traitId: action.traitId,
      traitLabel: action.traitLabel,
      role: extra?.role,
      roleLabel: extra?.roleLabel,
      fields: extra?.fields,
      roleExplicitlySelected: extra?.roleExplicitlySelected,
    };
    const persistedUserMessage = buildEntityGenerateUserMessageFromCtaAction(action.userMessage, ctaActionPayload);

    // generate_cast first asks how many characters; floating progress runs after that answer.
    const castAwaitingCharacterCount = action.type === "generate_cast";
    if (!castAwaitingCharacterCount) {
      clearStoryBibleCtaAutoPromotedFromChat();
    }
    setCtaAction({
      ...action,
      status: castAwaitingCharacterCount ? "awaiting_input" : "in_progress",
      // Since status stays awaiting_input, InstantFireInput stays mounted; instantFired is
      // what stops a remounted instance from re-sending this activation.
      ...(castAwaitingCharacterCount ? { instantFired: true } : {}),
      customInstructions: resolvedCustomInstructions || undefined,
      role: extra?.role,
      roleLabel: extra?.roleLabel,
      roleExplicitlySelected: extra?.roleExplicitlySelected,
    });
    const isEntityGenerate = action.type === "generate_character" || action.type === "generate_worldbuilding";
    const messageToSend = isEntityGenerate ? persistedUserMessage : action.userMessage;
    sendMessage(messageToSend, userSettings.getModelKey(), sendMode, false, undefined, {
      ctaAction: ctaActionPayload,
    });
    sentThisSessionRef.current = true;
    setTimeout(() => scrollToNewMessage(), 50);
  }, [setCtaAction, sendMessage, userSettings, updateUserSettings, scrollToNewMessage]);

  const handleCtaCancel = useCallback(() => {
    if (!ctaAction) return;
    restoreComposerToChatOnlyIfStoryBibleCtaCancelledBeforeRun(ctaAction.status, {
      onModeChange: () => updateUserSettings({ agenticChatMode: "chat" }),
    });
    setCtaAction({ ...ctaAction, status: "cancelled" });
    if (ctaAction.signalKey) {
      useCrossAppSignalStore.getState().setSignalActive(ctaAction.signalKey, false);
    }
  }, [ctaAction, setCtaAction, updateUserSettings]);

  const planActionParams = useMemo(() => {
    if (!activePlan) return null;
    return {
      chatId,
      projectId,
      planId: activePlan.id,
      modelKey: userSettings.getModelKey(),
      mode: userSettings.agenticChatMode as "write" | "chat",
      setChatId,
    };
  }, [activePlan, chatId, projectId, userSettings, setChatId]);

  const handlePlanApprove = useCallback(
    (removedStepIds: string[]) => {
      if (!planActionParams) return;
      approvePlan({ ...planActionParams, removedStepIds });
    },
    [planActionParams, approvePlan],
  );

  const handlePlanCancel = useCallback(async () => {
    if (!planActionParams) return;
    await cancelPlan(planActionParams);
  }, [planActionParams, cancelPlan]);

  const handleCommentingCancel = useCallback(() => {
    abortAgentStreamAndLinkedManuscriptFeedback("feedback_cancel");
    cancelCommenting();
  }, [abortAgentStreamAndLinkedManuscriptFeedback, cancelCommenting]);

  const handleStoryBibleCtaCancel = useCallback(() => {
    abortAgentStreamAndLinkedManuscriptFeedback("cta_cancel");
    const action = usePersistentChatStore.getState().ctaAction;
    if (!action) return;
    restoreComposerToChatOnlyIfStoryBibleCtaCancelledBeforeRun(action.status, {
      onModeChange: () => updateUserSettings({ agenticChatMode: "chat" }),
    });
    setCtaAction({ ...action, status: "cancelled" });
    if (action.signalKey) {
      useCrossAppSignalStore.getState().setSignalActive(action.signalKey, false);
    }
  }, [abortAgentStreamAndLinkedManuscriptFeedback, setCtaAction, updateUserSettings]);

  const handlePlanPause = useCallback(async () => {
    if (!planActionParams) return;
    await pausePlan(planActionParams);
  }, [planActionParams, pausePlan]);

  const handlePlanResume = useCallback(() => {
    if (!planActionParams) return;
    resumePlan(planActionParams);
  }, [planActionParams, resumePlan]);

  const handlePlanEditModeChange = useCallback((isEditing: boolean) => {
    setIsPlanEditing(isEditing);
    if (!isEditing) {
      // Prevent stale full-height measurement from creating a large spacer right after save.
      setPlanCardHeight(0);
    }
  }, []);

  const handlePlanEditSave = useCallback(
    async (
      steps: PlanData["steps"],
      planEdits?: { description: string; specialConsiderations?: string },
    ) => {
      if (!activePlan) return;
      const stepsWithStartedFirstTask: PlanData["steps"] = steps.map((step) => ({ ...step }));
      const existingInProgressIndex = stepsWithStartedFirstTask.findIndex((step) => step.status === "in_progress");
      if (existingInProgressIndex === -1) {
        const firstPendingIndex = stepsWithStartedFirstTask.findIndex((step) => step.status === "pending");
        if (firstPendingIndex !== -1) {
          stepsWithStartedFirstTask[firstPendingIndex] = {
            ...stepsWithStartedFirstTask[firstPendingIndex],
            status: "in_progress",
          };
        }
      }
      const currentStepIndex = stepsWithStartedFirstTask.findIndex((step) => step.status === "in_progress");
      const hasWorkRemaining = stepsWithStartedFirstTask.some((step) => step.status === "pending" || step.status === "in_progress");
      const effectivePlan = {
        ...activePlan,
        description: planEdits?.description ?? activePlan.description,
        specialConsiderations: planEdits?.specialConsiderations,
        steps: stepsWithStartedFirstTask,
        status: (hasWorkRemaining ? "in_progress" : "completed") as PlanData["status"],
        currentStepIndex:
          currentStepIndex >= 0
            ? currentStepIndex
            : Math.max(0, Math.min(activePlan.currentStepIndex ?? 0, Math.max(stepsWithStartedFirstTask.length - 1, 0))),
      };
      const previousPlan = activePlan;
      setPlan(effectivePlan);

      const stepLines = stepsWithStartedFirstTask
        .map((step, idx) => {
          const descriptionSuffix = step.description?.trim() ? ` — ${step.description.trim()}` : "";
          return `${idx + 1}. ${step.title}${descriptionSuffix}`;
        })
        .join("\n");

      const reviewPrompt = [
        "I just updated the plan. Treat these edits as intentional and already approved by the user.",
        "Do not ask the user to reconfirm removed, skipped, or reordered steps.",
        "Continue executing this updated plan directly.",
        "",
        `Plan goal: ${effectivePlan.description}`,
        ...(effectivePlan.specialConsiderations?.trim()
          ? [`Special considerations: ${effectivePlan.specialConsiderations.trim()}`]
          : []),
        "",
        "Updated steps:",
        stepLines || "(No steps)",
      ].join("\n");

      try {
        await sendMessage(
          reviewPrompt,
          userSettings.getModelKey(),
          userSettings.agenticChatMode as "write" | "chat",
          true,
          undefined,
          { keepProposedPlan: true, editedPlanApproved: true, editedPlan: effectivePlan },
        );
      } catch (error) {
        Sentry.captureException(error);
        const currentPlan = usePersistentChatStore.getState().activePlan;
        // Only roll back the optimistic in_progress bump if the client never received
        // real plan execution progress (chunk-based step updates). If steps moved past
        // the pre-send snapshot, keep the latest client state to avoid clobbering progress.
        const stillMatchesOptimisticSnapshot =
          currentPlan?.id === effectivePlan.id
          && plansEquivalentForMessageSync(currentPlan, effectivePlan);
        if (stillMatchesOptimisticSnapshot) {
          setPlan(previousPlan);
        }
      }
    },
    [activePlan, setPlan, sendMessage, userSettings],
  );

  useEffect(() => {
    const ctaActive = usePersistentChatStore.getState().ctaAction?.status === "awaiting_input";
    if (!ctaActive && !showUserQuestionOverlay) {
      setTimeout(() => {
        if (usePersistentChatStore.getState().currentUserQuestion) return;
        composerRef.current?.focus();
      }, 200);
    }
  }, [showUserQuestionOverlay]);

  /** Move focus into the question panel so Tab/arrow keys work without trapping in the hidden composer. */
  const userQuestionOverlayWasOpenRef = useRef(false);
  useEffect(() => {
    const inputShell = inputBoxRef.current;
    if (!showUserQuestionOverlay) {
      inputShell?.removeAttribute("inert");
      userQuestionOverlayWasOpenRef.current = false;
      return;
    }
    inputShell?.setAttribute("inert", "");
    const active = document.activeElement;
    if (active instanceof HTMLElement && inputShell?.contains(active)) {
      active.blur();
    }
    const shouldFocusFirstOption = !userQuestionOverlayWasOpenRef.current;
    userQuestionOverlayWasOpenRef.current = true;
    if (!shouldFocusFirstOption) return;
    let cancelled = false;
    const outerRaf = requestAnimationFrame(() => {
      const innerRaf = requestAnimationFrame(() => {
        if (cancelled) return;
        const firstOption = document.getElementById("uq-row-0");
        if (firstOption instanceof HTMLElement) {
          firstOption.focus({ preventScroll: true });
        }
      });
      if (cancelled) cancelAnimationFrame(innerRaf);
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(outerRaf);
    };
  }, [showUserQuestionOverlay]);

  const composerContainerRef = useRef<HTMLDivElement>(null);
  const activeHighlightChipRef = useRef<HTMLDivElement>(null);

  const disabledForTrial = isTrialFreeChatBlocked(trialFreeChatSubscriptionView);

  useEffect(() => {
    if (discussModeAvailable || isTrialFreeChatBlocked(trialFreeChatSubscriptionView)) return;
    if (agenticChatMode !== "chat") return;
    updateUserSettings({ agenticChatMode: "write" });
  }, [
    agenticChatMode,
    discussModeAvailable,
    trialFreeChatSubscriptionView,
    updateUserSettings,
  ]);

  const switchBranchMutate = switchBranchMutation.mutate;
  const switchToBranch = useCallback(
    (leafMessageId: string) => {
      usePersistentChatStore.setState({ queuedSend: null });
      switchBranchMutate(
        { chatId, leafMessageId },
        { onError: () => setToast("Couldn't switch thread. Try again.") },
      );
    },
    [switchBranchMutate, chatId, setToast],
  );

  return (
    <WithReset
      ref={cardsContainerRef}
      boxOnly
      className={cn(
        "flex-1 w-full flex-col gap-1 relative cards-container h-[calc(100%-3rem)] slide-in-bottom",
        isComposerModeMenuOpen && "overflow-visible",
        isPlanEditing || composerCoversNav ? "z-[60]" : "z-10",
      )}
    >
      <WithResetMotion
        initial={{ opacity: 0, y: 40 }}
        animate={{
          opacity: 1,
          y: 12,
          paddingBottom: inputHeight + 10 + (showUserQuestionOverlay ? USER_QUESTION_SCROLL_RESERVE : 0),
        }}
        exit={{ opacity: 0, y: 40 }}
        transition={{ duration: 0.2, delay: 0.3, paddingBottom: { duration: 0.15, delay: 0 } }}
        className="flex-1 w-full h-full pt-3"
      >
        <SharedChatPanel>
          {messages.length > 0 ? (
          <div
            ref={messagesContainerRef}
            className={cn(
              "h-full overflow-y-auto -mr-2 pt-14",
              "[scrollbar-width:thin] [scrollbar-color:transparent_transparent]",
              "hover:[scrollbar-color:var(--scrollbar-thumb)_transparent]",
              "[&::-webkit-scrollbar]:w-[6px]",
              "[&::-webkit-scrollbar-track]:bg-transparent",
              "[&::-webkit-scrollbar-thumb]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:transition-colors [&::-webkit-scrollbar-thumb]:duration-200",
              "[&:hover::-webkit-scrollbar-thumb]:bg-[var(--scrollbar-thumb)]",
            )}
          >
            <div className="pr-2">
              <SharedChatMessagesList
                gap="$space0"
                paddingHorizontal="$space0"
                paddingVertical="$space0"
              >
                <div ref={contentRef}>
                {hasMoreOlderMessages ? (
                  <div
                    ref={loadOlderSentinelRef}
                    className="flex justify-center py-3 text-app-grey-1"
                    aria-live="polite"
                  >
                    <Text className="text-xs">
                      {isLoadingOlderMessages ? "Loading earlier messages…" : "Scroll up for earlier messages"}
                    </Text>
                  </div>
                ) : null}
                {messages.map((message, index) => {
                  const isActivePersistedAssistant =
                    agentIsRunning && message.role === "assistant" && persistedStreamingMessage?.id === message.id;
                  const isLastMessage = index === messages.length - 1 && !agentIsRunning;
                  const isLastAssistantMessage =
                    message.role === "assistant" && message.id === lastAssistantMessageId;
                  const isRevertedStoryBibleChange = message.role === "assistant" && (revertedAssistantMessageIds.has(message.id) || message.metadata?.storyBibleRevertApplied === true);
                  const { revertOption, revertTargetAssistantMessageId, showRevertButton: isLastRevertibleAssistantMessage } = resolveRevertVisibility({
                    messageId: message.id,
                    role: message.role,
                    revertOptionsByAssistantMessageId,
                    revertedAssistantMessageIds,
                    isRevertedStoryBibleChange,
                  });

                  // Hide user messages that are answers to a question — the answer is shown inline on the assistant message.
                  const prevMessage = index > 0 ? messages[index - 1] : null;
                  if (message.role === "user" && prevMessage?.metadata?.questionAsked) {
                    const prevRawIdx = _messages.findIndex((m) => m.id === prevMessage.id);
                    const currRawIdx = _messages.findIndex((m) => m.id === message.id);
                    const hasHiddenAnswerBetween = prevRawIdx !== -1 && currRawIdx !== -1 && _messages.slice(prevRawIdx + 1, currRawIdx).some((m) => m.metadata?.hiddenFromUI);
                    if (!hasHiddenAnswerBetween) {
                      return null;
                    }
                  }

                  let questionAnswer: AppendedDocumentMentionsResult | null = null;
                  if (message.metadata?.questionAsked) {
                    const preview = (message.metadata as { questionAnswerPreview?: string })?.questionAnswerPreview;
                    const previewHasAppendedDocumentMentions =
                      (message.metadata as { questionAnswerPreviewHasAppendedDocumentMentions?: boolean })?.questionAnswerPreviewHasAppendedDocumentMentions === true;
                    if (preview?.trim()) {
                      questionAnswer = {
                        text: preview,
                        hasAppendedDocumentMentions: previewHasAppendedDocumentMentions,
                      };
                    } else {
                      const nextMessage = index < messages.length - 1 ? messages[index + 1] : null;
                      if (nextMessage?.role === "user") {
                        questionAnswer = {
                          text: nextMessage.content,
                          hasAppendedDocumentMentions: nextMessage.metadata?.appendedDocumentMentions === true,
                        };
                      } else {
                        const rawIdx = _messages.findIndex((m) => m.id === message.id);
                        if (rawIdx !== -1) {
                          const nextRaw = _messages[rawIdx + 1];
                          if (nextRaw?.role === "user") {
                            questionAnswer = {
                              text: nextRaw.content,
                              hasAppendedDocumentMentions: nextRaw.metadata?.appendedDocumentMentions === true,
                            };
                          }
                        }
                        if (questionAnswer === null && submittedAnswer !== null && submittedAnswer.questionMessageId === message.id) {
                          questionAnswer = submittedAnswer;
                        }
                      }
                    }
                  }

                  const messageToolRuns = (() => {
                    const cached = savedToolRunsCache.current.get(message.id);
                    if (cached) return cached;
                    const runs = (message.metadata?.toolExecutions || []).map((te: any, idx: number) => ({
                      callId: `saved-${message.id}-${idx}`,
                      name: te.name,
                      status: "complete" as const,
                      summary: te.summary,
                    }));
                    savedToolRunsCache.current.set(message.id, runs);
                    return runs;
                  })();

                  const branchVariants =
                    message.role === "user"
                      ? userBranchVariantsAtParent(branchRawMessages as any, message.parent_message_id ?? null)
                        .filter((candidate) => candidate.id === message.id || userMessageIdsWithVisibleAssistantReplySet.has(candidate.id))
                      : [];
                  const branchIdx = branchVariants.findIndex((v) => v.id === message.id);
                  const branchCount = branchVariants.length;

                  const disableUserBranchPrevWhileStreaming =
                    agentIsRunning
                    && message.id === tailAwaitingReplyUserMessageId;

                  const historicalEntityCtaAction =
                    message.role === "assistant"
                      ? entityGenerateCtaActionFromUserMessage(
                          _messages.find((m) => m.id === message.metadata?.triggerUserMessageId),
                          message.metadata?.ctaActionType,
                        )
                      : undefined;

                  return (
                    <Fragment key={getMessageRenderKey(message, index)}>
                      <div
                        data-message
                        data-role={message.role}
                        className={cn(
                          "py-1",
                          isLastMessage
                            ? ((suggestedAction || currentUserQuestion) && !streaming) ||
                              (ctaAction && (ctaAction.status === "awaiting_input" || ctaAction.status === "cancelled")) ||
                              showCommentingCardInline || !!stalledConversationState
                              ? "pb-1"
                              : "pb-10"
                            : "",
                        )}
                      >
                        <PersistentChatMessage
                          message={message}
                          showInlineTerminalPlan={terminalPlanCardMessageIds.has(message.id)}
                          showInlineTerminalCommenting={terminalCommentingCardMessageIds.has(message.id)}
                          streaming={isActivePersistedAssistant || (commentingInProgress && isLastAssistantMessage)}
                          lookingAtData={isActivePersistedAssistant ? activeTurnLookingAtData : EMPTY_STRING_ARRAY}
                          searchedDocs={isActivePersistedAssistant ? activeTurnSearchedDocs : EMPTY_STRING_ARRAY}
                          cutoffData={isActivePersistedAssistant ? activeTurnCutoffData : undefined}
                          contextWarning={isActivePersistedAssistant ? activeTurnContextWarning : undefined}
                          toolRuns={isActivePersistedAssistant ? activeTurnToolRuns : messageToolRuns}
                          outlineUpdates={isActivePersistedAssistant ? activeTurnOutlineUpdates : (message.metadata?.outlineUpdates || EMPTY_ARRAY)}
                          characterUpdates={isActivePersistedAssistant ? activeTurnCharacterUpdates : (message.metadata?.characterUpdates || EMPTY_ARRAY)}
                          worldbuildingUpdates={isActivePersistedAssistant ? activeTurnWorldbuildingUpdates : (message.metadata?.worldbuildingUpdates || EMPTY_ARRAY)}
                          metaUpdates={isActivePersistedAssistant ? activeTurnMetaUpdates : (message.metadata?.metaUpdates || EMPTY_ARRAY)}
                          thinkingSteps={isActivePersistedAssistant ? activeTurnThinkingSteps : (message.metadata?.thinkingSteps || EMPTY_ARRAY)}
                          revertOption={revertOption}
                          revertTargetAssistantMessageId={revertTargetAssistantMessageId}
                          canShowInlineEditForUserMessageId={canShowInlineEditForUserMessageId}
                          onEditTailUserMessage={handleStartEditUserMessage}
                          inlineEditUserMessageId={inlineEditUserMessageId}
                          onCancelInlineEditUserMessage={() => setInlineEditUserMessageId(null)}
                          onSubmitInlineEditUserMessage={handleSubmitInlineEditUserMessage}
                          isLastMessage={isLastMessage}
                          isLastAssistantMessage={isLastAssistantMessage}
                          isLastRevertibleAssistantMessage={isLastRevertibleAssistantMessage}
                          isRevertedStoryBibleChange={isRevertedStoryBibleChange}
                          onRevertStoryBibleSuccess={handleRevertStoryBibleSuccess}
                          streamingModel={isActivePersistedAssistant ? activeTurnModel : undefined}
                          streamingWritePovTenseChiclets={isActivePersistedAssistant ? activeTurnWritePovTenseChiclets : undefined}
                          streamingContextElements={isActivePersistedAssistant ? activeTurnContextElements : undefined}
                          streamingMentionedEntities={isActivePersistedAssistant ? activeTurnMentionedEntities : undefined}
                          mode={message.metadata?.mode as "write" | "chat" | undefined}
                          initialRating={feedbackByMessageId?.[message.id] ?? null}
                          questionAnswer={questionAnswer}
                          ctaAction={historicalEntityCtaAction}
                          onCtaGo={undefined}
                          onCtaCancel={undefined}
                          onResendLastMessage={handleResendLastMessage}
                          onDuplicatePlanRetry={handleDuplicatePlanRetry}
                          activeDocumentId={activeDocumentId}
                          activeProjectId={projectId}
                          onRetryCommentCreation={
                            message.role === "assistant" && message.metadata?.commentCreationCorrectionApplied
                              ? () => handleRetryCommentCreation(message.metadata?.triggerUserMessageId as string, "", (message.metadata?.mode as "write" | "chat") ?? "write")
                              : undefined
                          }
                          feedbackByMessageId={feedbackByMessageId}
                          onFeedbackTryAgain={handleFeedbackTryAgain}
                          onFeedbackSwitchToChatMode={handleFeedbackSwitchToChatMode}
                          onFeedbackStartFreshChat={handleFeedbackStartFreshChat}
                          userBranchSiblingIndex={branchCount > 1 && branchIdx >= 0 ? branchIdx : undefined}
                          userBranchSiblingCount={branchCount > 1 ? branchCount : undefined}
                          onUserBranchPrev={
                            branchCount > 1 && branchIdx > 0 && chatId !== NEW_CHAT_ID && !disableUserBranchPrevWhileStreaming
                              ? () => {
                                  const prev = branchVariants[branchIdx - 1];
                                  const tip = branchTipMessageId(branchRawMessages as any, prev.id);
                                  switchToBranch(tip);
                                }
                              : undefined
                          }
                          onUserBranchNext={
                            branchCount > 1 && branchIdx >= 0 && branchIdx < branchCount - 1 && chatId !== NEW_CHAT_ID
                              ? () => {
                                  const next = branchVariants[branchIdx + 1];
                                  const tip = branchTipMessageId(branchRawMessages as any, next.id);
                                  switchToBranch(tip);
                                }
                              : undefined
                          }
                        />
                      </div>
                      {isRevertedStoryBibleChange ? (
                        <div className="mt-1 mb-2 pb-0.5 px-3 flex items-center gap-1.5">
                          <InlineSVG
                            src="/assets/check-simple.svg"
                            className="w-4 h-4 [&_path]:fill-app-grey-1"
                            aria-hidden="true"
                          />
                          <Text
                            size="sm"
                            className="text-app-grey-1"
                          >
                            Reverted Story Bible changes from this message.
                          </Text>
                        </div>
                      ) : null}
                    </Fragment>
                  );
                })}
                <StreamingMessage
                  persistedMessage={messages.find(message => message.id === persistedStreamingMessage?.id) ?? persistedStreamingMessage}
                  mode={userSettings.agenticChatMode as "write" | "chat"}
                  suggestedAction={suggestedAction}
                  ctaAction={ctaAction}
                  feedbackByMessageId={feedbackByMessageId}
                  activeDocumentId={activeDocumentId}
                  activeProjectId={projectId}
                />
                {queuedSend?.resendIntent === "retry" && (
                  <div role="status" className="pb-4">
                    <ThinkingAccordion
                      title="Resending"
                      titleStreaming
                      expanded={false}
                      collapsible={false}
                      leading={thinkingStepChromeIcons.streamingSparkleAnimated}
                      icons={thinkingStepChromeIcons}
                    />
                  </div>
                )}
                {failedFollowUp && (
                  <div role="alert" className="pb-4 px-1 text-app-grey-1">
                    <Text size="sm">{failedFollowUp.message}</Text>
                    <button
                      type="button"
                      className="mt-2 underline"
                      onClick={() => {
                        const params = failedFollowUp.params;
                        if (failedFollowUp.chatUnavailable) {
                          params.setChatId(NEW_CHAT_ID);
                          void usePersistentChatStore.getState().sendMessage({ ...params, chatId: NEW_CHAT_ID, startNewChat: true,
                            currentMessages: [], resendIntent: undefined, resendOfMessageId: undefined, parentMessageId: undefined,
                            replacementCommandId: undefined, replacesClientRequestId: undefined, priorClientRequestIds: undefined, targetClientRequestId: undefined });
                        } else void usePersistentChatStore.getState().sendMessage(params);
                      }}
                    >
                      {failedFollowUp.chatUnavailable ? "Send in new chat" : "Retry sending"}
                    </button>
                  </div>
                )}
                {!!stalledConversationState && !inlineEditUserMessageId && !showCommentingCardInline && (
                  <div className="pb-10 px-0.5">
                    <WithReset
                      className="w-full flex items-center justify-between px-3 py-3 rounded-xl border border-solid border-app-grey-3 hover:border-app-grey-2 cursor-pointer transition-all active:scale-[0.99]"
                      onClick={handleResendLastMessage}
                      aria-label="Resend last message"
                    >
                      <Text size="base">Resend message</Text>
                      <InlineSVG
                        className="w-5 h-5 flex-shrink-0 [&_path]:fill-app-primary-cta-high-contrast"
                        src="/assets/send.svg"
                      />
                    </WithReset>
                  </div>
                )}
                {showCommentingCardInline && activeCommenting && (
                  <div className="pb-10 px-0.5">
                    <CommentingCard
                      commenting={activeCommenting}
                      mode="inline"
                    />
                    <WithReset
                      className="mt-2 w-full flex items-center justify-between px-3 py-3 rounded-xl border border-solid border-app-grey-3 hover:border-app-grey-2 cursor-pointer transition-all active:scale-[0.99]"
                      onClick={handleResendLastMessage}
                      aria-label="Resend last message"
                    >
                      <Text size="base">Resend message</Text>
                      <InlineSVG
                        className="w-5 h-5 flex-shrink-0 [&_path]:fill-app-primary-cta-high-contrast"
                        src="/assets/send.svg"
                      />
                    </WithReset>
                  </div>
                )}
                {ctaAction && (ctaAction.status === "awaiting_input" || ctaAction.status === "cancelled") && (
                  <div className="py-1">
                    <ThinkingStep
                      data={{ id: "cta-standalone", title: ctaAction.displayTitle, tasks: [], status: "running" as const }}
                      ctaAction={ctaAction}
                      onCtaGo={handleCtaGo}
                      onCtaCancel={handleCtaCancel}
                    />
                  </div>
                )}
                <AnimatePresence>
                  {suggestedAction && !streaming && (
                    <SuggestedActionCard
                      action={suggestedAction}
                      currentMode={userSettings.agenticChatMode as "write" | "chat"}
                      writeModeBadgeLabel={composerModeLabels.writeLabel}
                      onActionClick={handleSuggestedActionClick}
                    />
                  )}
                </AnimatePresence>
                {showFloatingPlan && planCardHeight > 0 && (
                  <div
                    style={{ height: planCardHeight }}
                    className="flex-shrink-0"
                  />
                )}
                {isFloatingCommentingVisible && commentingCardHeight > 0 && (
                  <div
                    style={{ height: commentingCardHeight }}
                    className="flex-shrink-0"
                  />
                )}
                {isFloatingStoryBibleCta && storyBibleCtaCardHeight > 0 && (
                  <div
                    style={{ height: storyBibleCtaCardHeight }}
                    className="flex-shrink-0"
                  />
                )}
                {chatExperienceSurveyScrollReserve > 0 && (
                  <div
                    data-testid="chat-experience-survey-scroll-reserve"
                    style={{ height: chatExperienceSurveyScrollReserve }}
                    className="flex-shrink-0"
                    aria-hidden="true"
                  />
                )}
                </div>
                <div
                  ref={spacerRef}
                  className="flex-shrink-0"
                  aria-hidden="true"
                />
              </SharedChatMessagesList>
            </div>
          </div>
        ) : (
          <SharedChatMessagesList
            gap="$space0"
            paddingHorizontal="$space0"
            paddingVertical="$space0"
          >
            <div className="flex flex-col h-full min-h-0">
              <div className="flex-1 min-h-0 flex flex-col">
                {ctaAction?.type === "generate_cast" && ctaAction.status === "in_progress" ? (
                  <div className="flex-1 flex flex-col items-center justify-center px-8 pt-14 text-center gap-2">
                    <Text className="text-app-grey-1 text-lg font-medium">Generating all characters</Text>
                    <Text className="text-app-grey-2 text-sm">
                      Watch progress in your Story Bible and the card below.
                    </Text>
                  </div>
                ) : !(ctaAction && (ctaAction.status === "in_progress" || ctaAction.status === "awaiting_input")) ? (
                  <div className="flex-1 flex flex-col items-center justify-center w-full min-h-0 px-4">
                    <ConversationStarters
                      isNewProject={isNewProject}
                      currentMode={userSettings.agenticChatMode as "write" | "chat"}
                      importConversationStarters={project?.data?.importConversationStarters ?? null}
                      importReportDocumentId={gatedImportReportDocumentId}
                      importConversationStartersDismissedForReport={project?.data?.importConversationStartersDismissedForReport}
                      writeModeBadgeLabel={composerModeLabels.writeLabel}
                      onStarterClick={handleStarterClick}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </SharedChatMessagesList>
        )}
        </SharedChatPanel>
      </WithResetMotion>

      <AnimatePresence>
        {showFloatingPlan && activePlan && (
          <div
            ref={planCardRef}
            className={cn("absolute w-full", isPlanEditing ? "z-[60] bg-app-menu-color" : "z-10")}
            style={{
              bottom: isPlanEditing ? "0px" : `${inputHeight + 4}px`,
              ...(isPlanEditing ? { top: `${messagesTopOffset}px` } : {}),
            }}
          >
            <PlanCard
              plan={activePlan}
              mode="floating"
              resumeInFlight={planResumeRequestInFlight}
              pauseInFlight={pausePlanApiRequestInFlight}
              onApprove={handlePlanApprove}
              onCancel={handlePlanCancel}
              onPause={handlePlanPause}
              onResume={handlePlanResume}
              onEditModeChange={handlePlanEditModeChange}
              onSaveEdits={handlePlanEditSave}
            />
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isFloatingCommentingVisible && activeCommenting && (
          <div
            ref={commentingCardRef}
            className="absolute w-full z-10"
            style={{
              bottom: `${inputHeight + (showFloatingPlan && planCardHeight > 0 ? planCardHeight + 8 : 4)}px`,
            }}
          >
            <CommentingCard
              commenting={activeCommenting}
              mode="floating"
              onCancel={handleCommentingCancel}
            />
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isFloatingStoryBibleCta && ctaAction && (
          <div
            ref={storyBibleCtaCardRef}
            className="absolute w-full z-10"
            style={{
              bottom: `${inputHeight + (showFloatingPlan && planCardHeight > 0 ? planCardHeight + 8 : 4) + (isFloatingCommentingVisible ? commentingCardHeight : 0)}px`,
            }}
          >
            <StoryBibleCtaCard
              ctaAction={ctaAction}
              mode="floating"
              onCancel={handleStoryBibleCtaCancel}
            />
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {mountChatExperienceSurvey && (
          <div
            ref={chatExperienceSurveyRef}
            className={cn("absolute w-full", !showChatExperienceSurvey && "hidden")}
            style={{
              bottom: `${inputHeight + floatingCardsOffsetAboveInput + 8}px`,
              zIndex: FLOATING_CHAT_LAYER_Z_INDEX.experienceSurvey,
            }}
          >
            <ChatExperienceSurvey
              onSubmit={handleChatExperienceSurveySubmit}
              onSkip={handleChatExperienceSurveySkip}
            />
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {!isAtBottom && messages.length > 0 && (
          <motion.button
            key="scroll-to-bottom"
            initial={{ opacity: 0, y: 10, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.8 }}
            whileTap={{ scale: 0.9 }}
            transition={{
              y: SPRING_BOUNCY,
              scale: SPRING_BOUNCY,
              opacity: FADE_FAST,
            }}
            onClick={() => scrollToBottom()}
            className={cn(
              "absolute left-1/2 -translate-x-1/2",
              "w-10 h-10 rounded-full",
              "bg-app-primary-cta-50 border-2 border-app-primary-cta-transparent border-solid",
              "hover:bg-app-primary-cta-100 hover:border-app-primary-cta-100",
              "flex items-center justify-center",
              "cursor-pointer outline-none",
              "transition-colors",
            )}
            style={{
              bottom: `${inputHeight + floatingCardsOffsetAboveInput + 8}px`,
              zIndex: FLOATING_CHAT_LAYER_Z_INDEX.scrollToBottom,
            }}
            aria-label="Scroll to bottom"
          >
            <InlineSVG
              src="/assets/arrow-down.svg"
              className="w-4 h-4 text-app-primary-cta-high-contrast"
            />
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showImportFeedbackOverlay && visibleImportFeedback && (
          <BlockingBottomSheetOverlay
            overlayKey="import-feedback-overlay"
            titleId="import-feedback-title"
            withDropShadow
          >
            <ImportFeedback
              variant="bottomSheet"
              variation={visibleImportFeedback.variation}
              invocationId={visibleImportFeedback.invocationId}
              cohort={visibleImportFeedback.cohort}
              onSubmit={handleImportFeedbackSubmit}
              onSkip={handleImportFeedbackSkip}
            />
          </BlockingBottomSheetOverlay>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showUserQuestionOverlay && currentUserQuestion && (
          <BlockingBottomSheetOverlay
            overlayKey="ask-user-question-overlay"
            titleId="ask-user-question-title"
          >
            <AskUserQuestion
              key={currentUserQuestion.questions[0]?.question ?? ""}
              variant="bottomSheet"
              questions={currentUserQuestion.questions}
              onSubmit={sendUserQuestionResponse}
              onSkip={handleSkipUserQuestion}
            />
          </BlockingBottomSheetOverlay>
        )}
      </AnimatePresence>

      <WithResetMotion
        ref={inputBoxRef}
        animate={{ opacity: showBlockingBottomOverlay ? 0 : 1 }}
        transition={{ opacity: FADE_FAST }}
        className={cn(
          "w-full absolute bottom-0 gap-1",
          isComposerModeMenuOpen && "z-30 overflow-visible",
          showBlockingBottomOverlay && "pointer-events-none select-none",
        )}
        style={{
          display: "flex",
          flexDirection: "column-reverse",
          justifyContent: "flex-start",
        }}
        aria-hidden={showBlockingBottomOverlay}
      >
        {!isComposerModeMenuOpen && (
          <div className="w-full -top-8 absolute h-8 bg-gradient-to-b from-transparent to-app-menu-color blur-lg z-20" />
        )}

        {disabledForTrial && <div className="h-10 flex items-center justify-center font-bold text-center italic text-app-grey-0">To keep chatting, sign up for a plan.</div>}
        {!disabledForTrial && (
          <>
            {queuedSend && queuedSend.resendIntent !== "retry" && (
              <div role="status" className="px-3 py-2 text-sm">
                Message queued
                <button type="button" className="ml-2 underline" onClick={() => usePersistentChatStore.setState({ queuedSend: null })}>Cancel</button>
              </div>
            )}

            <SharedChatComposerShell embedded>
              <ChatComposer
                key={chatId ?? NEW_CHAT_ID}
                ref={composerRef}
                agentIsRunning={agentIsRunning}
                sendQueued={!!queuedSend}
                activePlan={activePlan}
                ctaAction={ctaAction}
                agenticChatMode={agenticChatMode}
                modeLabel={modeLabel}
                onAgenticChatModeChange={onAgenticChatModeChange}
                isAdmin={isAdmin}
                agenticChatBrainModel={agenticChatBrainModel}
                adminBrainModelOptions={adminBrainModelOptions}
                onAgenticChatBrainModelChange={onAgenticChatBrainModelChange}
                modeLabelVariant={modeLabelVariant}
                discussModeAvailable={discussModeAvailable}
                onSend={handleComposerSend}
                onStopAgent={handleStopAgent}
                onAttachmentsChange={handleComposerAttachmentsChange}
                onComposerKeyDownCapture={handleComposerKeyDownCapture}
                activeHighlightChipRef={activeHighlightChipRef}
                containerRef={composerContainerRef}
                onModeMenuOpenChange={setIsComposerModeMenuOpen}
              />
            </SharedChatComposerShell>
            <AnimatePresence>
              {showModeSwitchBackToast && discussModeAvailable && (
                <motion.div
                  key="cta-mode-switch-toast"
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 12 }}
                  transition={{ y: SPRING_BOUNCY, opacity: FADE_FAST }}
                  className="relative z-20 w-full"
                >
                  <BottomActionToast
                    onDismiss={() => {
                      clearStoryBibleCtaAutoPromotedFromChat();
                      setShowModeSwitchBackToast(false);
                    }}
                    message={
                      <span className="inline whitespace-normal break-words">
                        <InlineSVG
                          src="/assets/pencil.svg"
                          className="mr-2 inline-block h-4 w-4 align-middle [&_path]:fill-current"
                          aria-hidden
                        />
                        <span className="align-middle">You're editing. </span>
                        <span
                          role="button"
                          tabIndex={0}
                          className="inline cursor-pointer border-0 bg-transparent p-0 align-middle text-sm font-inherit font-semibold text-app-primary-cta-high-contrast hover:underline"
                          onClick={() => {
                            AppEventPublisher.agenticChat.modeSwitch(AgenticChatMode.Write, AgenticChatMode.Chat);
                            updateUserSettings({ agenticChatMode: "chat" });
                            clearStoryBibleCtaAutoPromotedFromChat();
                            setShowModeSwitchBackToast(false);
                          }}
                          onKeyDown={(event) => {
                            if (event.key !== "Enter" && event.key !== " ") return;
                            event.preventDefault();
                            AppEventPublisher.agenticChat.modeSwitch(AgenticChatMode.Write, AgenticChatMode.Chat);
                            updateUserSettings({ agenticChatMode: "chat" });
                            clearStoryBibleCtaAutoPromotedFromChat();
                            setShowModeSwitchBackToast(false);
                          }}
                        >
                          Switch to {composerModeLabels.chatLabel}
                        </span>
                      </span>
                    }
                  />
                </motion.div>
              )}
              {showLanguagePreferenceToast && (
                <motion.div
                  key={`language-preference-${languagePreferenceToast.type}`}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 12 }}
                  transition={{ y: SPRING_BOUNCY, opacity: FADE_FAST }}
                  className="relative z-20 w-full"
                >
                  <BottomActionToast
                    onDismiss={() => {
                      dismissLanguagePreferenceToast(languagePreferenceToast.languageCode, "toast_close");
                    }}
                    message={
                      languagePreferenceToast.type === "saved" ? (
                        <span>Preference saved.</span>
                      ) : (
                        <span>Save a preference to reply in {getLanguageDisplayName(languagePreferenceToast.languageCode)} for all chat responses and outputs?</span>
                      )
                    }
                    actions={
                      languagePreferenceToast.type === "prompt"
                        ? [
                            {
                              label: "No",
                              variant: "tertiary",
                              onClick: () => dismissLanguagePreferenceToast(languagePreferenceToast.languageCode, "no_button"),
                              ariaLabel: "Dismiss language preference prompt",
                            },
                            {
                              label: languagePreferenceSaving ? "Saving..." : "Yes",
                              variant: "primary",
                              onClick: handleLanguagePreferenceSave,
                              disabled: languagePreferenceSaving,
                              ariaLabel: "Save language preference",
                            },
                          ]
                        : [
                            {
                              label: "Open chat preferences",
                              variant: "tertiary",
                              onClick: openChatPreferencesFromToast,
                              ariaLabel: "Open chat preferences",
                            },
                          ]
                    }
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}

        <AnimatePresence>
          {inlineToast && (
            <WithResetMotion
              initial={{ opacity: 0, y: 16, scale: 0.95, filter: "blur(4px)" }}
              animate={{ opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }}
              exit={{ opacity: 0, y: -8, scale: 0.95, filter: "blur(4px)" }}
              transition={{
                duration: 0.35,
                ease: [0.32, 0.72, 0, 1],
              }}
              className="flex items-center gap-2 px-3 py-2 mb-2 w-fit self-center z-30 rounded-[5px] bg-[var(--grey-0)] shadow-[var(--default-drop-shadow)]"
            >
              <InlineSVG
                src="/assets/check-simple.svg"
                className="w-4 h-4 [&_path]:fill-white"
              />
              <span className="font-medium text-sm text-[var(--context-menu-text)]">{inlineToast}</span>
            </WithResetMotion>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {activeHighlight?.trim() ? (
            <WithReset
              ref={activeHighlightChipRef}
              data-agentic-chat-highlight-chip="true"
              className="relative group"
            >
              <WithReset
                as="button"
                ref={activeHighlightDismissButtonRef}
                type="button"
                aria-label="Remove highlighted selection"
                onClick={() => {
                  dismissActiveHighlightFromComposer();
                }}
                className={cn(
                  "group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100 opacity-0 transition-all z-20",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-app-primary-cta",
                  "hover:scale-105 active:scale-95 duration-300 ease-in-out cursor-pointer",
                  "absolute top-2 right-2",
                  "rounded-full w-5 h-5 bg-app-primary-cta-translucent",
                  "flex items-center justify-center",
                )}
              >
                <InlineSVG
                  src="/assets/close.svg"
                  className="w-4 h-4 [&_path]:fill-app-grey-1 group-hover:opacity-100 group-focus-within:opacity-100 opacity-0 transition-all"
                />
              </WithReset>

              <WithReset className="px-3 -mb-1 overflow-hidden relative group">
                <div className="left-3 right-3 bottom-0 absolute h-5 bg-gradient-to-b from-transparent to-app-menu-color blur-sm z-20" />

                <WithResetMotion
                  boxOnly
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 12 }}
                  exit={{ opacity: 0, y: 40 }}
                  transition={{ duration: 0.2 }}
                  className={cn("flex flex-row gap-2 items-center py-2 flex-wrap px-3 relative rounded-t-xl bg-app-primary-cta-translucent z-0")}
                >
                  <Text
                    truncate
                    className="font-gt-super-text max-w-full line-clamp-3 text-sm italic text-app-grey-1"
                  >
                    “{activeHighlight.slice(0, 100)}
                  </Text>
                </WithResetMotion>
              </WithReset>
            </WithReset>
          ) : null}
        </AnimatePresence>

        <ComposerAttachmentStrip
          docs={composerAttachmentDocs}
          onRemove={handleRemoveComposerAttachment}
        />
      </WithResetMotion>
    </WithReset>
  );
};
